import React, { useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Brain,
  CalendarDays,
  CheckCircle2,
  Clock3,
    RotateCcw,
  Sparkles,
  Target,
  Printer,
  Send
} from 'lucide-react';

import {
  getMufredatVerisi,
  kaynaklarHavuzu,
  MUFREDAT_MODELLERI
} from '../data/coaching/coachingCatalog';

import {
  generateSmartPlan,
  isSmartPlanPartial,
  isSmartPlanReady
} from '../utils/coaching/smartPlanEngine';

import {
  SMART_PLAN_STRATEGIES,
  SMART_PLAN_STRATEGY_OPTIONS
} from '../utils/coaching/smartPlanStrategies';

import {
  SMART_PLAN_EXAM_MODES,
  SMART_PLAN_FIELDS,
  SMART_PLAN_PRIORITY_LEVELS,
  SMART_PLAN_TOPIC_STATUS,
  getAllowedSubjects
} from '../utils/coaching/smartPlanRules';


const DAYS = [
  'Pazartesi',
  'Salı',
  'Çarşamba',
  'Perşembe',
  'Cuma',
  'Cumartesi',
  'Pazar'
];

const EXAM_OPTIONS = [
  { value: SMART_PLAN_EXAM_MODES.TYT, label: 'TYT' },
  { value: SMART_PLAN_EXAM_MODES.AYT, label: 'AYT' },
  { value: SMART_PLAN_EXAM_MODES.TYT_AYT, label: 'TYT + AYT' },
  { value: SMART_PLAN_EXAM_MODES.LGS, label: 'LGS' }
];

const CURRICULUM_OPTIONS = [
  {
    value:
      MUFREDAT_MODELLERI.MEVCUT,
    label:
      'Mevcut / Klasik Müfredat',
    description:
      'Rehber360’ın mevcut TYT / AYT konu kataloğunu kullanır.'
  },
 {
  value:
    MUFREDAT_MODELLERI.MAARIF,
  label:
    'Türkiye Yüzyılı Maarif Modeli',
  description:
    'Türkiye Yüzyılı Maarif Modeline göre güncellenmiş TYT / AYT konu kataloğunu kullanır.'
}
];

const FIELD_OPTIONS = [
  { value: SMART_PLAN_FIELDS.SAYISAL, label: 'Sayısal' },
  { value: SMART_PLAN_FIELDS.ESIT_AGIRLIK, label: 'Eşit Ağırlık' },
  { value: SMART_PLAN_FIELDS.SOZEL, label: 'Sözel' }
];

const PRIORITY_OPTIONS = [
  { value: SMART_PLAN_PRIORITY_LEVELS.NORMAL, label: 'Normal' },
  { value: SMART_PLAN_PRIORITY_LEVELS.PRIORITY, label: 'Öncelikli' },
  { value: SMART_PLAN_PRIORITY_LEVELS.VERY_HIGH, label: 'Çok Öncelikli' }
];


const DAILY_SUBJECT_LIMIT_OPTIONS = [
  {
    value: null,
    label: 'Otomatik',
    description: 'Robot ders çeşitliliğini planın yapısına göre belirler.'
  },
  {
    value: 2,
    label: 'En fazla 2 ders',
    description: 'Bir çalışma gününde en fazla 2 farklı ders yer alır.'
  },
  {
    value: 3,
    label: 'En fazla 3 ders',
    description: 'Bir çalışma gününde en fazla 3 farklı ders yer alır.'
  },
  {
    value: 4,
    label: 'En fazla 4 ders',
    description: 'Bir çalışma gününde en fazla 4 farklı ders yer alır.'
  }
];

const TOPIC_STATUS_OPTIONS = [
  { value: SMART_PLAN_TOPIC_STATUS.NOT_STARTED, label: 'Başlanmadı' },
  { value: SMART_PLAN_TOPIC_STATUS.INCOMPLETE, label: 'Eksik / Pekişmeli' },
  { value: SMART_PLAN_TOPIC_STATUS.COMPLETED, label: 'Tamamlandı' }
];

const TASK_LABELS = {
  new_topic: 'Yeni Konu',
  practice: 'Soru Çözümü',
  review: 'Planlı Tekrar',
  mixed_practice: 'Karma Soru',

  topic_scan: 'Konu Tarama',
  weekly_scan: 'Haftalık Tarama',

  branch_mock: 'Branş Denemesi',
  full_mock: 'Tam Deneme',
  mock_analysis: 'Deneme Analizi',
  buffer: 'Telafi / Esnek Alan',
  routine: 'Kısa Pekiştirme'
};


const MANUAL_TASK_TYPE_OPTIONS = [
  'new_topic',
  'practice',
  'review',
  'routine',
  'mixed_practice',
  'buffer'
];


const createManualTaskDraft = () => ({
  subject: '',
  topic: '',
  type: 'practice',
  duration: 30,
  questionCount: '',
  resource: ''
});

const TASK_STYLES = {
  new_topic: 'border-indigo-200 bg-indigo-50 text-indigo-800',
  practice: 'border-blue-200 bg-blue-50 text-blue-800',
  review: 'border-violet-200 bg-violet-50 text-violet-800',
  mixed_practice: 'border-cyan-200 bg-cyan-50 text-cyan-800',

  topic_scan: 'border-teal-200 bg-teal-50 text-teal-800',
  weekly_scan: 'border-sky-200 bg-sky-50 text-sky-800',

  branch_mock: 'border-amber-200 bg-amber-50 text-amber-800',
  full_mock: 'border-orange-200 bg-orange-50 text-orange-800',
  mock_analysis: 'border-rose-200 bg-rose-50 text-rose-800',
  buffer: 'border-slate-200 bg-slate-50 text-slate-700',
  routine: 'border-emerald-200 bg-emerald-50 text-emerald-800'
};


const TASK_DESCRIPTIONS = {
  new_topic:
    'Yeni içeriği öğrenin ve konunun temel noktalarını anlamaya odaklanın.',

  practice:
    'Yeni öğrendiğiniz bilgiyi sorular üzerinde uygulayın ve anlamanızı kontrol edin.',

  review:
    'Daha önce çalıştığınız konuyu yeniden hatırlayın ve eksik kalan noktaları kontrol edin.',

  routine:
    'Öğrendiğiniz konuyu kısa bir çalışmayla canlı tutun ve akıcılığınızı koruyun.',

  mixed_practice:
    'Farklı konu ve becerileri birlikte kullanarak karma soru çalışması yapın.',

  topic_scan:
    'Çalıştığınız konuyu kısa bir taramayla kontrol edin ve hangi noktaların yeniden ele alınması gerektiğini görün.',

  weekly_scan:
    'Bu hafta çalıştığınız konuları kısa bir taramayla birlikte değerlendirin ve öğrenme durumunuzu kontrol edin.',

  buffer:
    'Eksik kalan çalışmaları tamamlayın veya ihtiyaç duyduğunuz kısa bir telafi çalışması yapın.'
};

// =========================================================
// SCOPED ASSESSMENT — UI KAPSAM YARDIMCILARI
// =========================================================

const getScopedAssessmentTopicGroups =
  task => {

    if (
      task?.type !== 'topic_scan' &&
      task?.type !== 'weekly_scan'
    ) {
      return [];
    }


    const assessmentTopics =
      Array.isArray(
        task?.assessmentTopics
      )
        ? task.assessmentTopics
        : [];


    const groupedTopics =
      new Map();


    assessmentTopics.forEach(
      item => {

        const subject =
          typeof item?.subject === 'string'
            ? item.subject.trim()
            : '';

        const topic =
          typeof item?.topic === 'string'
            ? item.topic.trim()
            : '';


        if (
          !subject ||
          !topic
        ) {
          return;
        }


        if (
          !groupedTopics.has(
            subject
          )
        ) {
          groupedTopics.set(
            subject,
            []
          );
        }


        const subjectTopics =
          groupedTopics.get(
            subject
          );


        if (
          !subjectTopics.includes(
            topic
          )
        ) {
          subjectTopics.push(
            topic
          );
        }
      }
    );


    return [
      ...groupedTopics.entries()
    ].map(
      ([subject, topics]) => ({
        subject,
        topics
      })
    );
  };


const renderScopedAssessmentScope =
  task => {

    const groups =
      getScopedAssessmentTopicGroups(
        task
      );


    const topicCount =
      groups.reduce(
        (total, group) =>
          total +
          group.topics.length,
        0
      );


    if (
      topicCount === 0
    ) {
      return null;
    }


    return (
      <div className="mt-2 rounded-lg border border-current/10 bg-white/60 px-2.5 py-2">

        <p className="text-[9px] font-black uppercase tracking-wide opacity-60">
          Tarama kapsamı · {topicCount} konu
        </p>


        <div className="mt-1.5 space-y-1.5">

          {groups.map(
            group => (

              <div
                key={group.subject}
              >

                <p className="text-[10px] font-black">
                  {group.subject}
                </p>

                <p className="text-[10px] mt-0.5 leading-relaxed opacity-75">
                  {group.topics.join(' • ')}
                </p>

              </div>
            )
          )}

        </div>

      </div>
    );
  };



const ISSUE_MESSAGES = {
  INVALID_WEEK_COUNT: 'Plan süresi geçerli aralıkta değil.',
  INVALID_DAILY_MINUTES: 'Günlük çalışma süresi geçerli aralıkta değil.',
  INVALID_WORKING_DAY_COUNT: 'En az bir çalışma günü seçilmelidir.',
  DUPLICATE_WORKING_DAY: 'Aynı çalışma günü birden fazla kez seçilmiş.',
  FIELD_REQUIRED: 'AYT içeren planlarda alan seçimi zorunludur.',
  INVALID_STRATEGY: 'Geçerli bir çalışma stratejisi seçilmelidir.',
  SUBJECT_REQUIRED: 'En az bir ders seçilmelidir.',
  SUBJECT_NOT_ALLOWED_FOR_EXAM: 'Seçilen derslerden biri sınav türü veya alanla uyumlu değil.',
  SUBJECT_NOT_FOUND_IN_CURRICULUM: 'Seçilen derslerden biri müfredat kataloğunda bulunamadı.',
  INVALID_SUBJECT_PRIORITY: 'Ders önceliklerinden biri geçersiz.',
  TOPICS_FOR_UNSELECTED_SUBJECT: 'Seçilmemiş bir derse konu gönderildi.',
    TOPIC_NOT_FOUND_IN_CURRICULUM:
    'Seçilen konulardan biri müfredatta bulunamadı.',

  NO_PLAN_SLOTS_GENERATED:
    'Bu ayarlarla anlamlı bir çalışma bloğu üretilemedi.',

   TOPIC_COVERAGE_INSUFFICIENT:
    'Seçilen konu kapsamının tamamı mevcut plan süresine sığmıyor. Plan süresini artırmanız veya konu kapsamını daraltmanız önerilir.',

  PLAN_HAS_UNSCHEDULED_SLOTS:
    'Planın bazı çalışma blokları takvime yerleştirilemedi.',

  NO_COMPLETE_FLEXIBLE_SCHEDULE_FOUND:
    'Seçilen ayarlarla tüm çalışma bloklarını kapsayan eksiksiz bir takvim oluşturulamadı.',

  UNSCHEDULED_SLOTS:
    'Takvime yerleştirilemeyen çalışma blokları bulunuyor.',

  UNUSED_PLAN_CAPACITY:
  'Seçilen konu kapsamı, belirlenen çalışma kapasitesinin tamamını anlamlı çalışmalarla doldurmak için dar kalmış olabilir. Daha dolu bir plan için daha fazla konu seçebilir veya planı manuel olarak düzenleyebilirsiniz.',

  SUBJECT_CLUSTERED_CONSECUTIVE_DAYS:
    'Bazı derslere ait çalışmalar, öğrenme sırasını korumak için birbirine yakın günlerde planlanmıştır.'
};
const STEPS = [
  'Öğrenci & Sınav',
  'Çalışma Kapasitesi',
  'Dersler & Konular',
  'Strateji',
  'Plan Önizleme'
];


// =========================================================
// AKILLI PLAN — MILD HİBRİT EDİTÖR YARDIMCILARI
// =========================================================

const prepareEditableSchedule = schedule => {

  if (!Array.isArray(schedule)) {
    return [];
  }

  return schedule.map(
    (week, weekIndex) => ({

      ...week,

      days: Array.isArray(week?.days)
        ? week.days.map(
            (day, dayIndex) => {

              const tasks =
                Array.isArray(day?.tasks)
                  ? day.tasks.map(
                      (task, taskIndex) => ({

                        ...task,

                        editorId:
                          task?.editorId ||
                          `engine-${
                            week?.weekNumber ??
                            weekIndex + 1
                          }-${
                            day?.globalDayIndex ??
                            dayIndex
                          }-${
                            task?.id ??
                            taskIndex
                          }`,

                        origin:
                          task?.origin ||
                          'engine',

                        editedByUser:
                          Boolean(
                            task?.editedByUser
                          )

                      })
                    )
                  : [];


              return {

                ...day,

                tasks,

                taskCount:
                  tasks.length,

                totalMinutes:
                  tasks.reduce(
                    (sum, task) =>
                      sum +
                      Number(
                        task?.duration ||
                        0
                      ),
                    0
                  )

              };
            }
          )
        : []

    })
  );
};


const cloneEditableSchedule = schedule =>
  prepareEditableSchedule(
    schedule
  );


const recalculateEditableDay = (
  day,
  tasks
) => {

  const safeTasks =
    Array.isArray(tasks)
      ? tasks
      : [];

  return {

    ...day,

    tasks:
      safeTasks,

    taskCount:
      safeTasks.length,

    totalMinutes:
      safeTasks.reduce(
        (sum, task) =>
          sum +
          Number(
            task?.duration ||
            0
          ),
        0
      )

  };
};


const createInitialConfig = () => ({
  weeks: 4,
  examMode: SMART_PLAN_EXAM_MODES.TYT,

  curriculumModel:
    MUFREDAT_MODELLERI.MEVCUT,

  field: null,
  strategyId: 'dengeli',
  dailyMinutes: 120,

  maxSubjectsPerDay: null,

  workingDays: ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma'],
   selectedSubjects: [],
  subjectPriorities: {},
  selectedTopics: {},
  topicStatuses: {},

  resourcePreferences: {},

  difficulty: 'Orta',
  studentLevel: 'Orta'
});


const SmartPlanBuilder = ({
  students = [],
  user,
  submissions = [],
  onAssign,
  academicYear = '2026-2027'
}) => {
  const [step, setStep] = useState(0);
  const [studentKey, setStudentKey] = useState('');
    const [config, setConfig] = useState(createInitialConfig);

  const [result, setResult] =
    useState(null);

  const [
    originalSchedule,
    setOriginalSchedule
  ] = useState([]);

    const [
    editableSchedule,
    setEditableSchedule
  ] = useState([]);


  const [
    editHistory,
    setEditHistory
  ] = useState([]);


  const [openSubject, setOpenSubject] =
    useState('');


  const [
    manualTaskTarget,
    setManualTaskTarget
  ] = useState(null);


  const [
    manualTaskDraft,
    setManualTaskDraft
  ] = useState(
    createManualTaskDraft
  );


const [isAssigning, setIsAssigning] =
  useState(false);

const [assignSuccess, setAssignSuccess] =
  useState(false);


const [
  assignWarningConfirmOpen,
  setAssignWarningConfirmOpen
] = useState(false);


const needsField =
    config.examMode === SMART_PLAN_EXAM_MODES.AYT ||
    config.examMode === SMART_PLAN_EXAM_MODES.TYT_AYT;

  const allowedSubjects = useMemo(
    () => getAllowedSubjects({ examMode: config.examMode, field: config.field }),
    [config.examMode, config.field]
  );


const activeMufredat =
  useMemo(
    () =>
      getMufredatVerisi(
        config.curriculumModel
      ),
    [
      config.curriculumModel
    ]
  );

    const selectedStudent = useMemo(
    () => students.find(item => `${item.className}__${item.no}` === studentKey) || null,
    [students, studentKey]
  );


  const pushEditHistory = () => {

    const snapshot =
      cloneEditableSchedule(
        editableSchedule
      );


        setEditHistory(
      previousHistory => [

        ...previousHistory.slice(
          -29
        ),

        snapshot

      ]
    );


    setAssignWarningConfirmOpen(
      false
    );
  };


  const clearPlanResult = () => {

    if (result) {
      setResult(null);
    }

            setOriginalSchedule([]);
    setEditableSchedule([]);
    setEditHistory([]);

    setManualTaskTarget(null);
    setManualTaskDraft(
      createManualTaskDraft()
    );
  };



  const getManualTaskTopics = subject => {

    const explicitlySelected =
      Array.isArray(
        config.selectedTopics?.[
          subject
        ]
      )
        ? config.selectedTopics[
            subject
          ]
        : [];


    if (
      explicitlySelected.length > 0
    ) {
      return explicitlySelected;
    }


    return Array.isArray(
  activeMufredat?.[
    subject
  ]
)
  ? activeMufredat[
      subject
    ]
  : [];
  };


  const openManualTaskForm = ({
    weekNumber,
    day
  }) => {





    const defaultSubject =
      config.selectedSubjects?.[0] ||
      '';


       const defaultTopics =
      getManualTaskTopics(
        defaultSubject
      );


    setManualTaskDraft({
      ...createManualTaskDraft(),

      subject:
        defaultSubject,

      topic:
        defaultTopics[0] ||
        ''
    });


        setManualTaskTarget({
      mode: 'add',
      weekNumber,
      day,
      editorId: null
    });
  };


   const closeManualTaskForm = () => {

      setManualTaskTarget(null);

    setManualTaskDraft(
      createManualTaskDraft()
    );

    setAssignWarningConfirmOpen(
      false
    );
  };


  const openEditTaskForm = ({
    weekNumber,
    day,
    task
  }) => {

    if (!task?.editorId) {
      return;
    }


    if (
      !MANUAL_TASK_TYPE_OPTIONS.includes(
        task?.type
      )
    ) {

      alert(
        'Bu görev türü şu an hızlı düzenleme formuyla desteklenmiyor.'
      );

      return;
    }


    setManualTaskDraft({

      subject:
        task?.subject ||
        config.selectedSubjects?.[0] ||
        '',

      topic:
        task?.topic ||
        '',

      type:
        task?.type ||
        'practice',

      duration:
        Number(
          task?.duration
        ) || 30,

      questionCount:
        task?.questionCount ??
        '',

      resource:
        task?.resource ||
        ''

    });


    setManualTaskTarget({

      mode:
        'edit',

      weekNumber,

      day,

      editorId:
        task.editorId

    });
  };


  const handleMoveTask = ({
    editorId,
    targetWeekNumber,
    targetDay
  }) => {

        if (
      !editorId ||
      !targetWeekNumber ||
      !targetDay
    ) {
      return;
    }


    pushEditHistory();


    setEditableSchedule(
      previousSchedule => {

        let sourceTask = null;
        let sourceWeekNumber = null;
        let sourceDay = null;


        for (
          const week of
          previousSchedule
        ) {

          for (
            const day of
            week?.days || []
          ) {

            const foundTask =
              (
                day?.tasks ||
                []
              ).find(
                task =>
                  task?.editorId ===
                  editorId
              );


            if (foundTask) {

              sourceTask =
                foundTask;

              sourceWeekNumber =
                week?.weekNumber;

              sourceDay =
                day?.day;

              break;
            }
          }


          if (sourceTask) {
            break;
          }
        }


        if (!sourceTask) {
          return previousSchedule;
        }


        if (
          Number(
            sourceWeekNumber
          ) ===
            Number(
              targetWeekNumber
            ) &&
          sourceDay ===
            targetDay
        ) {
          return previousSchedule;
        }


        const targetExists =
          previousSchedule.some(
            week =>
              Number(
                week?.weekNumber
              ) ===
                Number(
                  targetWeekNumber
                ) &&
              (
                week?.days ||
                []
              ).some(
                day =>
                  day?.day ===
                  targetDay
              )
          );


        if (!targetExists) {
          return previousSchedule;
        }


        return previousSchedule.map(
          week => ({

            ...week,

            days:
              (
                week?.days ||
                []
              ).map(
                day => {

                  const isSourceDay =
                    Number(
                      week?.weekNumber
                    ) ===
                      Number(
                        sourceWeekNumber
                      ) &&
                    day?.day ===
                      sourceDay;


                  const isTargetDay =
                    Number(
                      week?.weekNumber
                    ) ===
                      Number(
                        targetWeekNumber
                      ) &&
                    day?.day ===
                      targetDay;


                  if (
                    !isSourceDay &&
                    !isTargetDay
                  ) {
                    return day;
                  }


                  let nextTasks =
                    Array.isArray(
                      day?.tasks
                    )
                      ? [
                          ...day.tasks
                        ]
                      : [];


                  if (isSourceDay) {

                    nextTasks =
                      nextTasks.filter(
                        task =>
                          task?.editorId !==
                          editorId
                      );
                  }


                  if (isTargetDay) {

                    nextTasks.push({

                      ...sourceTask,

                      weekNumber:
                        week?.weekNumber,

                      day:
                        day?.day,

                      globalDayIndex:
                        day?.globalDayIndex ??
                        sourceTask?.globalDayIndex,

                      editedByUser:
                        true

                    });
                  }


                  return recalculateEditableDay(
                    day,
                    nextTasks
                  );
                }
              )

          })
        );
      }
    );


        setAssignSuccess(false);
  };


  const handleAddManualTask = () => {

    if (!manualTaskTarget) {
      return;
    }


    const taskType =
      manualTaskDraft.type;


    const requiresCurriculum =
      taskType !== 'buffer';


    const subject =
      manualTaskDraft.subject
        ?.trim() ||
      '';


    const topic =
      manualTaskDraft.topic
        ?.trim() ||
      '';


    const duration =
      Number(
        manualTaskDraft.duration
      );


    const questionCount =
      Number(
        manualTaskDraft.questionCount
      );


    const resource =
      manualTaskDraft.resource
        ?.trim() ||
      '';


    if (
      !taskType ||
      !Number.isFinite(duration) ||
      duration < 15
    ) {

      alert(
        'Görev türünü ve geçerli bir çalışma süresini seçiniz.'
      );

      return;
    }


    if (
      requiresCurriculum &&
      (
        !subject ||
        !topic
      )
    ) {

      alert(
        'Bu çalışma türü için ders ve konu seçiniz.'
      );

      return;
    }


        const manualId =
      `manual-${
        Date.now()
      }-${
        Math.random()
          .toString(36)
          .slice(2, 8)
      }`;


    pushEditHistory();


    setEditableSchedule(
      previousSchedule =>
        previousSchedule.map(
          week => {

            if (
              Number(
                week?.weekNumber
              ) !==
                Number(
                  manualTaskTarget
                    .weekNumber
                )
            ) {
              return week;
            }


            return {

              ...week,

              days:
                (
                  week?.days ||
                  []
                ).map(
                  day => {

                    if (
                      day?.day !==
                      manualTaskTarget.day
                    ) {
                      return day;
                    }


                    const manualTask = {

                      id:
                        manualId,

                      editorId:
                        manualId,

                      origin:
                        'manual',

                      editedByUser:
                        false,

                      type:
                        taskType,

                      subject:
                        requiresCurriculum
                          ? subject
                          : null,

                      topic:
                        requiresCurriculum
                          ? topic
                          : null,

                      duration,

                      questionCount:
                        Number.isFinite(
                          questionCount
                        ) &&
                        questionCount > 0
                          ? Math.floor(
                              questionCount
                            )
                          : null,

                      resource:
                        resource ||
                        null,

                      weekNumber:
                        week?.weekNumber,

                      day:
                        day?.day,

                      globalDayIndex:
                        day?.globalDayIndex ??
                        null,

                      reason:
                        'manual_user_task'
                    };


                    const nextTasks = [
                      ...(
                        day?.tasks ||
                        []
                      ),
                      manualTask
                    ];


                    return recalculateEditableDay(
                      day,
                      nextTasks
                    );
                  }
                )

            };
          }
        )
    );


        setAssignSuccess(false);

    closeManualTaskForm();
  };


  const handleUpdateTask = () => {

    if (
      manualTaskTarget?.mode !==
        'edit' ||
      !manualTaskTarget?.editorId
    ) {
      return;
    }


    const taskType =
      manualTaskDraft.type;


    const requiresCurriculum =
      taskType !== 'buffer';


    const subject =
      manualTaskDraft.subject
        ?.trim() ||
      '';


    const topic =
      manualTaskDraft.topic
        ?.trim() ||
      '';


    const duration =
      Number(
        manualTaskDraft.duration
      );


    const questionCount =
      Number(
        manualTaskDraft.questionCount
      );


    const resource =
      manualTaskDraft.resource
        ?.trim() ||
      '';


    if (
      !taskType ||
      !Number.isFinite(duration) ||
      duration < 15
    ) {

      alert(
        'Görev türünü ve geçerli bir çalışma süresini seçiniz.'
      );

      return;
    }


      if (
      requiresCurriculum &&
      (
        !subject ||
        !topic
      )
    ) {

      alert(
        'Bu çalışma türü için ders ve konu seçiniz.'
      );

      return;
    }


    pushEditHistory();


   


    setEditableSchedule(
      previousSchedule =>
        previousSchedule.map(
          week => {

            if (
              Number(
                week?.weekNumber
              ) !==
                Number(
                  manualTaskTarget
                    .weekNumber
                )
            ) {
              return week;
            }


            return {

              ...week,

              days:
                (
                  week?.days ||
                  []
                ).map(
                  dayItem => {

                    if (
                      dayItem?.day !==
                      manualTaskTarget.day
                    ) {
                      return dayItem;
                    }


                    const nextTasks =
                      (
                        dayItem?.tasks ||
                        []
                      ).map(
                        task => {

                          if (
                            task?.editorId !==
                            manualTaskTarget
                              .editorId
                          ) {
                            return task;
                          }


                          return {

                            ...task,

                            type:
                              taskType,

                            subject:
                              requiresCurriculum
                                ? subject
                                : null,

                            topic:
                              requiresCurriculum
                                ? topic
                                : null,

                            duration,

                            questionCount:
                              Number.isFinite(
                                questionCount
                              ) &&
                              questionCount > 0
                                ? Math.floor(
                                    questionCount
                                  )
                                : null,

                            resource:
                              resource ||
                              null,

                            editedByUser:
                              true

                          };
                        }
                      );


                    return recalculateEditableDay(
                      dayItem,
                      nextTasks
                    );
                  }
                )

            };
          }
        )
    );


    setAssignSuccess(false);

    closeManualTaskForm();
  };


  const handleRemoveTask = ({
    weekNumber,
    day,
    editorId
  }) => {

    if (!editorId) {
      return;
    }


    const confirmed =
      window.confirm(
        'Bu görevi plandan kaldırmak istiyor musunuz?'
      );


       if (!confirmed) {
      return;
    }


    pushEditHistory();


    setEditableSchedule(
      previousSchedule =>
        previousSchedule.map(
          week => {

            if (
              Number(
                week?.weekNumber
              ) !==
                Number(
                  weekNumber
                )
            ) {
              return week;
            }


            return {

              ...week,

              days:
                (
                  week?.days ||
                  []
                ).map(
                  dayItem => {

                    if (
                      dayItem?.day !==
                      day
                    ) {
                      return dayItem;
                    }


                    const nextTasks =
                      (
                        dayItem?.tasks ||
                        []
                      ).filter(
                        task =>
                          task?.editorId !==
                          editorId
                      );


                    return recalculateEditableDay(
                      dayItem,
                      nextTasks
                    );
                  }
                )

            };
          }
        )
    );


    if (
      manualTaskTarget?.mode ===
        'edit' &&
      manualTaskTarget?.editorId ===
        editorId
    ) {
      closeManualTaskForm();
    }


       setAssignSuccess(false);
  };


  const handleUndoEdit = () => {

    if (
      editHistory.length === 0
    ) {
      return;
    }


    const previousSchedule =
      editHistory[
        editHistory.length - 1
      ];


    setEditableSchedule(
      cloneEditableSchedule(
        previousSchedule
      )
    );


    setEditHistory(
      previousHistory =>
        previousHistory.slice(
          0,
          -1
        )
    );


        closeManualTaskForm();

    setAssignWarningConfirmOpen(
      false
    );

    setAssignSuccess(false);
  };


  const handleRestoreOriginalPlan = () => {

    if (
      !Array.isArray(
        originalSchedule
      ) ||
      originalSchedule.length === 0
    ) {
      return;
    }


    const confirmed =
      window.confirm(
        'Planı robotun ilk oluşturduğu haline döndürmek istiyor musunuz?\n\nYaptığınız manuel değişiklikler ekrandan kaldırılacak. Bu işlemi Geri Al ile geri çevirebilirsiniz.'
      );


    if (!confirmed) {
      return;
    }


    pushEditHistory();


    setEditableSchedule(
      cloneEditableSchedule(
        originalSchedule
      )
    );


    closeManualTaskForm();

    setAssignSuccess(false);
  };


  const handleExamModeChange = (examMode) => {
    setConfig(prev => ({
  ...prev,

  examMode,

  curriculumModel:
    examMode ===
      SMART_PLAN_EXAM_MODES.LGS
      ? MUFREDAT_MODELLERI.MEVCUT
      : prev.curriculumModel,

  field: null,
  selectedSubjects: [],
  subjectPriorities: {},
  selectedTopics: {},
  topicStatuses: {}
}));

    setOpenSubject('');
    clearPlanResult();
  };


  const handleCurriculumModelChange =
  curriculumModel => {

    setConfig(prev => ({
      ...prev,

      curriculumModel,

      // Müfredat değiştiğinde eski konu
      // seçimleri yeni katalogla karışmamalı.
      selectedTopics: {},
      topicStatuses: {}
    }));


    setOpenSubject('');

    clearPlanResult();
  };

  const handleFieldChange = (field) => {
    setConfig(prev => ({
      ...prev,
      field,
      selectedSubjects: [],
      subjectPriorities: {},
      selectedTopics: {},
      topicStatuses: {}
    }));

    setOpenSubject('');
    clearPlanResult();
  };

  const toggleWorkingDay = (day) => {
    setConfig(prev => {
      const exists = prev.workingDays.includes(day);

      return {
        ...prev,
        workingDays: exists
          ? prev.workingDays.filter(item => item !== day)
          : [...prev.workingDays, day]
      };
    });

    clearPlanResult();
  };

  const toggleSubject = (subject) => {
    setConfig(prev => {
      const exists = prev.selectedSubjects.includes(subject);

      if (!exists) {
        return {
          ...prev,
          selectedSubjects: [
            ...prev.selectedSubjects,
            subject
          ],

          subjectPriorities: {
            ...prev.subjectPriorities,
            [subject]:
              SMART_PLAN_PRIORITY_LEVELS.NORMAL
          }
        };
      }

      const nextPriorities = {
        ...prev.subjectPriorities
      };

      const nextTopics = {
        ...prev.selectedTopics
      };

      delete nextPriorities[subject];
      delete nextTopics[subject];

      const nextStatuses =
        Object.fromEntries(
          Object.entries(
            prev.topicStatuses || {}
          )
            .filter(
              ([key]) =>
                !key.startsWith(
                  `${subject}__`
                )
            )
        );

      return {
        ...prev,

        selectedSubjects:
          prev.selectedSubjects
            .filter(
              item =>
                item !== subject
            ),

        subjectPriorities:
          nextPriorities,

        selectedTopics:
          nextTopics,

        topicStatuses:
          nextStatuses
      };
    });

    if (openSubject === subject) {
      setOpenSubject('');
    }

    clearPlanResult();
  };

  const setSubjectPriority = (
    subject,
    priority
  ) => {
    setConfig(prev => ({
      ...prev,

      subjectPriorities: {
        ...prev.subjectPriorities,
        [subject]:
          priority
      }
    }));

    clearPlanResult();
  };

  const toggleTopic = (
    subject,
    topic
  ) => {
    setConfig(prev => {
      const current =
        Array.isArray(
          prev.selectedTopics?.[subject]
        )
          ? prev.selectedTopics[subject]
          : [];

      const exists =
        current.includes(topic);

      const nextList =
        exists
          ? current.filter(
              item =>
                item !== topic
            )
          : [
              ...current,
              topic
            ];

      const nextTopics = {
        ...prev.selectedTopics
      };

      if (
        nextList.length > 0
      ) {
        nextTopics[subject] =
          nextList;
      } else {
        delete nextTopics[subject];
      }

      return {
        ...prev,

        selectedTopics:
          nextTopics
      };
    });

        clearPlanResult();
  };


  const toggleResourcePreference = (
    subject,
    resource
  ) => {

    setConfig(prev => {

      const current =
        Array.isArray(
          prev.resourcePreferences?.[
            subject
          ]
        )
          ? prev.resourcePreferences[
              subject
            ]
          : [];


      const exists =
        current.includes(
          resource
        );


      const nextList =
        exists
          ? current.filter(
              item =>
                item !== resource
            )
          : [
              ...current,
              resource
            ];


      const nextPreferences = {
        ...(
          prev.resourcePreferences ||
          {}
        )
      };


      if (
        nextList.length > 0
      ) {

        nextPreferences[
          subject
        ] = nextList;

      } else {

        delete nextPreferences[
          subject
        ];
      }


      return {
        ...prev,

        resourcePreferences:
          nextPreferences
      };
    });


    clearPlanResult();
  };


  const setTopicStatus = (
    subject,
    topic,
    status
  ) => {
    const key =
      `${subject}__${topic}`;

    setConfig(prev => {
      const nextStatuses = {
        ...prev.topicStatuses
      };

      if (
        status ===
        SMART_PLAN_TOPIC_STATUS.NOT_STARTED
      ) {
        delete nextStatuses[key];
      } else {
        nextStatuses[key] =
          status;
      }

      return {
        ...prev,

        topicStatuses:
          nextStatuses
      };
    });

    clearPlanResult();
  };

  const canContinue = () => {
    if (step === 0) {
      return (
        Boolean(studentKey) &&
        (
          !needsField ||
          Boolean(config.field)
        )
      );
    }

    if (step === 1) {
      return (
        Number(config.weeks) >= 1 &&
        Number(config.weeks) <= 36 &&
        Number(config.dailyMinutes) >= 30 &&
        Number(config.dailyMinutes) <= 600 &&
        config.workingDays.length > 0
      );
    }

    if (step === 2) {
      return (
        config.selectedSubjects.length > 0
      );
    }

    return true;
  };

    const generatePlanFromConfig =
  generationConfig => {

    try {

      const nextResult =
        generateSmartPlan(
          generationConfig
        );


      const engineSchedule =
        prepareEditableSchedule(
          nextResult?.schedule
        );


      const editableCopy =
        cloneEditableSchedule(
          engineSchedule
        );


      setResult(
        nextResult
      );

      setOriginalSchedule(
        engineSchedule
      );

      setEditableSchedule(
        editableCopy
      );

      setEditHistory([]);

      setAssignWarningConfirmOpen(
        false
      );

      setAssignSuccess(false);

      setStep(4);
    }

    catch (error) {

      console.error(
        'SmartPlan UI generation error:',
        error
      );


      setResult({
        success: false,
        status: 'failed',

        errors: [
          {
            code:
              'UI_GENERATION_ERROR',

            message:
              error?.message
          }
        ],

        warnings: [],

        schedule: [],

        scheduledSlots: [],

        unscheduledSlots: []
      });


      setOriginalSchedule([]);
      setEditableSchedule([]);
      setEditHistory([]);

      setAssignWarningConfirmOpen(
        false
      );

      setAssignSuccess(false);

      setStep(4);
    }
  };


const handleGenerate = () => {

  generatePlanFromConfig(
    config
  );
};

   const resetBuilder = () => {

    setStep(0);

    setStudentKey('');

    setConfig(
      createInitialConfig()
    );

    setResult(null);

            setOriginalSchedule([]);
    setEditableSchedule([]);
    setEditHistory([]);

    setManualTaskTarget(null);
       setManualTaskDraft(
      createManualTaskDraft()
    );

    setAssignWarningConfirmOpen(
      false
    );

    setOpenSubject('');
  };

  const renderIssue = issue => {

  if (
    issue?.code ===
      'UNUSED_PLAN_CAPACITY'
  ) {

    const unusedMinutes =
      Number(
        issue?.minutes
      );


    const unusedMinutesText =
      Number.isFinite(
        unusedMinutes
      ) &&
      unusedMinutes > 0
        ? ` Yaklaşık ${Math.round(
            unusedMinutes
          )} dk çalışma kapasitesi kullanılmadı.`
        : '';


    return (
      'Seçilen konu kapsamı, belirlenen çalışma kapasitesinin tamamını anlamlı çalışmalarla doldurmak için dar kalmış olabilir.' +
      unusedMinutesText +
      ' Daha dolu bir plan için daha fazla konu seçebilir veya planı manuel olarak düzenleyebilirsiniz.'
    );
  }


  return (
    ISSUE_MESSAGES[
      issue?.code
    ] ||
    issue?.message ||
    issue?.code ||
    'Bilinmeyen bildirim'
  );
};


const visibleWarnings =
  Array.isArray(result?.warnings)
    ? result.warnings.filter(
        (issue, index, allWarnings) =>
          allWarnings.findIndex(
            otherIssue =>
              renderIssue(otherIssue) ===
              renderIssue(issue)
          ) === index
      )
    : [];


const topicCoverageRecommendation =
  result?.recommendations
    ?.topicCoverage ||
  null;


const hasTopicCoverageRecommendation =
  Boolean(
    topicCoverageRecommendation &&
    Number.isFinite(
      Number(
        topicCoverageRecommendation
          ?.minimumFeasibleWeeks
      )
    ) &&
    Number(
      topicCoverageRecommendation
        ?.minimumFeasibleWeeks
    ) > 0
  );


const coverageRecommendedWeeks =
  Number(
    topicCoverageRecommendation
      ?.minimumFeasibleWeeks
  );


const canApplyCoverageRecommendation =
  hasTopicCoverageRecommendation &&
  Number.isFinite(
    coverageRecommendedWeeks
  ) &&
  coverageRecommendedWeeks >
    Number(config.weeks) &&
  coverageRecommendedWeeks <= 36;


const handleApplyCoverageRecommendation =
  () => {

    if (
      !Number.isFinite(
        coverageRecommendedWeeks
      ) ||
      coverageRecommendedWeeks <=
        Number(config.weeks)
    ) {
      return;
    }


    if (
      coverageRecommendedWeeks > 36
    ) {

      alert(
        'Önerilen plan süresi 36 haftalık üst sınırı aşıyor. Konu kapsamını daraltmanız veya çalışma kapasitesini yeniden değerlendirmeniz önerilir.'
      );

      return;
    }


    const nextConfig = {

      ...config,

      weeks:
        coverageRecommendedWeeks

    };


    setConfig(
      nextConfig
    );


    generatePlanFromConfig(
      nextConfig
    );
  };


const livePlanWarnings = useMemo(
  () => {

    const warnings = [];

    const warningKeys =
      new Set();


    const pushUniqueWarning = (
      key,
      warning
    ) => {

      if (
        warningKeys.has(
          key
        )
      ) {
        return;
      }


      warningKeys.add(
        key
      );

      warnings.push(
        warning
      );
    };


    const dailyMinuteLimit =
      Number(
        config.dailyMinutes
      ) || 0;


    const subjectLimit =
      Number(
        config.maxSubjectsPerDay
      );


    // =========================================
    // PEDAGOJİK ZİNCİR DURUMU
    // =========================================

    const learnedTopics =
      new Set();

    const practicedTopics =
      new Set();


    const newTopicEntries =
      [];


    const practicePositionsByTopic =
      new Map();


    let sequenceIndex = 0;


    (
      editableSchedule ||
      []
    ).forEach(
      week => {

        (
          week?.days ||
          []
        ).forEach(
          day => {

            const tasks =
              Array.isArray(
                day?.tasks
              )
                ? day.tasks
                : [];


            // =========================================
            // 1. GÜNLÜK SÜRE KONTROLÜ
            // =========================================

            const totalMinutes =
              tasks.reduce(
                (sum, task) =>
                  sum +
                  Number(
                    task?.duration ||
                    0
                  ),
                0
              );


            if (
              dailyMinuteLimit > 0 &&
              totalMinutes >
                dailyMinuteLimit
            ) {

              warnings.push({

                code:
                  'DAILY_MINUTES_EXCEEDED',

                weekNumber:
                  week?.weekNumber,

                day:
                  day?.day,

                message:
                  `${
                    week?.weekNumber
                  }. Hafta · ${
                    day?.day
                  }: ${
                    totalMinutes
                  } dk planlandı; günlük hedef ${
                    dailyMinuteLimit
                  } dk.`

              });
            }


            // =========================================
            // 2. GÜNLÜK DERS ÇEŞİTLİLİĞİ
            // =========================================

            const distinctSubjects =
              new Set(
                tasks
                  .map(
                    task =>
                      task?.subject
                  )
                  .filter(Boolean)
              );


            if (
              Number.isFinite(
                subjectLimit
              ) &&
              subjectLimit > 0 &&
              distinctSubjects.size >
                subjectLimit
            ) {

              warnings.push({

                code:
                  'DAILY_SUBJECT_LIMIT_EXCEEDED',

                weekNumber:
                  week?.weekNumber,

                day:
                  day?.day,

                message:
                  `${
                    week?.weekNumber
                  }. Hafta · ${
                    day?.day
                  }: ${
                    distinctSubjects.size
                  } farklı ders bulunuyor; seçilen sınır en fazla ${
                    subjectLimit
                  } ders.`

              });
            }


            // =========================================
            // 3. AYNI GÜN TEKRAR + PEKİŞTİRME ÇAKIŞMASI
            // =========================================

            const consolidationTypesByTopic =
              new Map();


            tasks.forEach(
              task => {

                if (
                  task?.type !==
                    'review' &&
                  task?.type !==
                    'routine'
                ) {
                  return;
                }


                const subject =
                  task?.subject;

                const topic =
                  task?.topic;


                if (
                  !subject ||
                  !topic
                ) {
                  return;
                }


                const topicKey =
                  `${subject}__${topic}`;


                const types =
                  consolidationTypesByTopic.get(
                    topicKey
                  ) ||
                  new Set();


                types.add(
                  task.type
                );


                consolidationTypesByTopic.set(
                  topicKey,
                  types
                );
              }
            );


            consolidationTypesByTopic.forEach(
              (
                types,
                topicKey
              ) => {

                if (
                  !types.has(
                    'review'
                  ) ||
                  !types.has(
                    'routine'
                  )
                ) {
                  return;
                }


                const separatorIndex =
                  topicKey.indexOf(
                    '__'
                  );


                const subject =
                  separatorIndex >= 0
                    ? topicKey.slice(
                        0,
                        separatorIndex
                      )
                    : topicKey;


                const topic =
                  separatorIndex >= 0
                    ? topicKey.slice(
                        separatorIndex + 2
                      )
                    : '';


                pushUniqueWarning(
                  `review-routine-same-day-${
                    week?.weekNumber
                  }-${
                    day?.day
                  }-${topicKey}`,

                  {

                    code:
                      'REVIEW_ROUTINE_SAME_DAY',

                    weekNumber:
                      week?.weekNumber,

                    day:
                      day?.day,

                    message:
                      `${
                        week?.weekNumber
                      }. Hafta · ${
                        day?.day
                      }: ${
                        subject
                      } / ${
                        topic
                      } için Planlı Tekrar ve Kısa Pekiştirme aynı güne yerleştirilmiş.`

                  }
                );
              }
            );


            // =========================================
            // 4. ÖĞRENME ZİNCİRİ
            // =========================================

            tasks.forEach(
              task => {

                const position =
                  sequenceIndex;


                sequenceIndex += 1;


                const subject =
                  task?.subject;

                const topic =
                  task?.topic;


                if (
                  !subject ||
                  !topic
                ) {
                  return;
                }


                const topicKey =
                  `${subject}__${topic}`;


                const initialStatus =
                  config.topicStatuses?.[
                    topicKey
                  ] ||
                  SMART_PLAN_TOPIC_STATUS.NOT_STARTED;


                const isNotStarted =
                  initialStatus ===
                  SMART_PLAN_TOPIC_STATUS.NOT_STARTED;


                // Eksik / tamamlandı konusu,
                // plan başlamadan önce öğrenilmiş
                // kabul edilir.

                if (!isNotStarted) {

                  learnedTopics.add(
                    topicKey
                  );

                  practicedTopics.add(
                    topicKey
                  );
                }


                // -----------------------------------------
                // YENİ KONU
                // -----------------------------------------

                if (
                  task?.type ===
                  'new_topic'
                ) {

                  learnedTopics.add(
                    topicKey
                  );


                  if (
                    isNotStarted
                  ) {

                    newTopicEntries.push({

                      topicKey,

                      subject,

                      topic,

                      position,

                      weekNumber:
                        week?.weekNumber,

                      day:
                        day?.day

                    });
                  }


                  return;
                }


                // -----------------------------------------
                // SORU ÇÖZÜMÜ
                // -----------------------------------------

                if (
                  task?.type ===
                  'practice'
                ) {

                  const practicePositions =
                    practicePositionsByTopic.get(
                      topicKey
                    ) ||
                    [];


                  practicePositions.push(
                    position
                  );


                  practicePositionsByTopic.set(
                    topicKey,
                    practicePositions
                  );


                  if (
                    isNotStarted &&
                    !learnedTopics.has(
                      topicKey
                    )
                  ) {

                    pushUniqueWarning(
                      `practice-before-new-${topicKey}`,

                      {

                        code:
                          'PRACTICE_BEFORE_NEW_TOPIC',

                        weekNumber:
                          week?.weekNumber,

                        day:
                          day?.day,

                        message:
                          `${
                            week?.weekNumber
                          }. Hafta · ${
                            day?.day
                          }: ${
                            subject
                          } / ${
                            topic
                          } için Soru Çözümü, Yeni Konu çalışmasından önce yer alıyor.`

                      }
                    );

                    return;
                  }


                  practicedTopics.add(
                    topicKey
                  );


                  return;
                }


                // -----------------------------------------
                // PLANLI TEKRAR
                // -----------------------------------------

                if (
                  task?.type ===
                  'review'
                ) {

                  if (
                    isNotStarted &&
                    !learnedTopics.has(
                      topicKey
                    )
                  ) {

                    pushUniqueWarning(
                      `review-before-exposure-${topicKey}`,

                      {

                        code:
                          'REVIEW_BEFORE_EXPOSURE',

                        weekNumber:
                          week?.weekNumber,

                        day:
                          day?.day,

                        message:
                          `${
                            week?.weekNumber
                          }. Hafta · ${
                            day?.day
                          }: ${
                            subject
                          } / ${
                            topic
                          } için Planlı Tekrar, konu öğrenilmeden önce yer alıyor.`

                      }
                    );
                  }


                  return;
                }


                // -----------------------------------------
                // KISA PEKİŞTİRME
                // -----------------------------------------

                if (
                  task?.type ===
                  'routine'
                ) {

                  if (
                    isNotStarted &&
                    (
                      !learnedTopics.has(
                        topicKey
                      ) ||
                      !practicedTopics.has(
                        topicKey
                      )
                    )
                  ) {

                    pushUniqueWarning(
                      `routine-before-chain-${topicKey}`,

                      {

                        code:
                          'ROUTINE_BEFORE_LEARNING_CHAIN',

                        weekNumber:
                          week?.weekNumber,

                        day:
                          day?.day,

                        message:
                          `${
                            week?.weekNumber
                          }. Hafta · ${
                            day?.day
                          }: ${
                            subject
                          } / ${
                            topic
                          } için Kısa Pekiştirme, Yeni Konu + Soru Çözümü zinciri tamamlanmadan önce yer alıyor.`

                      }
                    );
                  }

                }

              }
            );

          }
        );

      }
    );


    // =========================================
    // 5. YENİ KONU SONRASI SORU ÇÖZÜMÜ VAR MI?
    // =========================================

    newTopicEntries.forEach(
      entry => {

        const practicePositions =
          practicePositionsByTopic.get(
            entry.topicKey
          ) ||
          [];


        const hasPracticeAfter =
          practicePositions.some(
            position =>
              position >
              entry.position
          );


        if (
          !hasPracticeAfter
        ) {

          pushUniqueWarning(
            `new-topic-without-practice-${entry.topicKey}`,

            {

              code:
                'NEW_TOPIC_WITHOUT_PRACTICE',

              weekNumber:
                entry.weekNumber,

              day:
                entry.day,

              message:
                `${
                  entry.weekNumber
                }. Hafta · ${
                  entry.day
                }: ${
                  entry.subject
                } / ${
                  entry.topic
                } için Yeni Konu çalışması var ancak sonrasında Soru Çözümü bulunmuyor.`

            }
          );
        }

      }
    );


    return warnings;
  },
  [
    editableSchedule,
    config.dailyMinutes,
    config.maxSubjectsPerDay,
    config.topicStatuses
  ]
);

const resultReady = isSmartPlanReady(result);
const resultPartial = isSmartPlanPartial(result);


// =========================================================
// AKILLI PLAN — YAZDIRMA MOTORU
// =========================================================

const escapePrintHtml = value =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');


// =========================================================
// SCOPED ASSESSMENT — YAZDIRMA KAPSAMI
// =========================================================

const buildPrintableAssessmentScope =
  task => {

    const groups =
      getScopedAssessmentTopicGroups(
        task
      );


    const topicCount =
      groups.reduce(
        (total, group) =>
          total +
          group.topics.length,
        0
      );


    if (
      topicCount === 0
    ) {
      return '';
    }


    return `
      <div class="task-assessment-scope">

        <div class="task-assessment-title">
          Tarama kapsamı · ${escapePrintHtml(
            topicCount
          )} konu
        </div>

        ${
          groups.map(
            group => `

              <div class="task-assessment-group">

                <strong>
                  ${escapePrintHtml(
                    group.subject
                  )}
                </strong>

                <span>
                  ${escapePrintHtml(
                    group.topics.join(
                      ' • '
                    )
                  )}
                </span>

              </div>

            `
          ).join('')
        }

      </div>
    `;
  };


const getPrintExamLabel = () =>
  EXAM_OPTIONS.find(
    option =>
      option.value ===
      config.examMode
  )?.label ||
  config.examMode ||
  '-';


const getPrintStrategyLabel = () =>
  SMART_PLAN_STRATEGY_OPTIONS.find(
    option =>
      option.value ===
      config.strategyId
  )?.label ||
  config.strategyId ||
  '-';


const buildPrintableTask = task => {

  const taskLabel =
    TASK_LABELS[task?.type] ||
    task?.type ||
    'Görev';


  const description =
    TASK_DESCRIPTIONS[
      task?.type
    ] || '';


  const questionAndResource = [
    task?.questionCount
      ? `${task.questionCount} soru`
      : '',

    task?.resource || ''
  ]
    .filter(Boolean)
    .join(' • ');


  return `
    <div class="task task-${escapePrintHtml(task?.type)}">

      <div class="task-top">

        <div class="task-type">
          <span class="checkbox">□</span>
          ${escapePrintHtml(taskLabel)}
        </div>

        <strong>
          ${escapePrintHtml(task?.duration || 0)} dk
        </strong>

      </div>

      ${
        task?.subject
          ? `
            <div class="task-subject">
              ${escapePrintHtml(task.subject)}
            </div>
          `
          : ''
      }

            ${
        task?.topic
          ? `
            <div class="task-topic">
              ${escapePrintHtml(task.topic)}
            </div>
          `
          : ''
      }

      ${buildPrintableAssessmentScope(
        task
      )}

      ${
        description
          ? `
            <div class="task-description">
              ${escapePrintHtml(description)}
            </div>
          `
          : ''
      }

      ${
        questionAndResource
          ? `
            <div class="task-meta">
              ${escapePrintHtml(questionAndResource)}
            </div>
          `
          : ''
      }

    </div>
  `;
};


const buildPrintableWeek = week => {

  const days =
    Array.isArray(week?.days)
      ? week.days
      : [];


  // =====================================================
  // PRINT DENSITY
  // =====================================================
  //
  // Amaç:
  //
  // - rahat haftaları gereksiz küçültmemek,
  // - yoğun 6 günlük haftaları mümkün olduğunca
  //   tek A4 landscape sayfada tutmak,
  // - aşırı yoğunlukta içeriği kesmemek.
  //
  // normal  → ferah görünüm
  // compact → orta / yüksek yoğunluk
  // dense   → yüksek yoğunluk
  // =====================================================

  const totalTaskCount =
    days.reduce(
      (total, day) =>
        total +
        (
          Array.isArray(
            day?.tasks
          )
            ? day.tasks.length
            : 0
        ),
      0
    );


  const maxTasksPerDay =
    days.reduce(
      (maximum, day) => {

        const taskCount =
          Array.isArray(
            day?.tasks
          )
            ? day.tasks.length
            : 0;


        return Math.max(
          maximum,
          taskCount
        );
      },
      0
    );


    const printDensityClass =
(
  maxTasksPerDay >= 5 ||
  totalTaskCount >= 22 ||

  (
    days.length >= 6 &&
    (
      maxTasksPerDay >= 4 ||
      totalTaskCount >= 15
    )
  )
)
  ? 'print-density-dense'

  : (
      maxTasksPerDay >= 4 ||
      totalTaskCount >= 15 ||
      (
        days.length >= 6 &&
        totalTaskCount >= 12
      )
    )
    ? 'print-density-compact'

    : 'print-density-normal';


  // =====================================================
  // PRINT DAY LAYOUT
  // =====================================================
  //
  // Gün sayısı ile grid yapısını birbirinden ayırıyoruz.
  //
  // 1 gün → 1 sütun
  // 2 gün → 2 sütun
  // 3 gün → 3 sütun
  // 4 gün → 2 + 2
  // 5 gün → 3 + 2
  // 6 gün → 3 + 3
  // 7 gün → 4 + 3
  //
  // Density ise yalnız kartların ne kadar sıkı
  // gösterileceğini belirlemeye devam eder.
  // =====================================================

  const printDayLayoutClass =
    days.length >= 1 &&
    days.length <= 7
      ? `print-days-${days.length}`
      : 'print-days-default';


  return `
    <section class="week-page ${printDensityClass} ${printDayLayoutClass}">

      <div class="week-title">

        <div>
          <div class="brand-small">
            REHBER360 • AKILLI PLAN
          </div>

          <h1>
            ${escapePrintHtml(
              week?.weekNumber
            )}. Hafta
          </h1>
        </div>

        <div class="week-day-count">
          ${days.length} çalışma günü
        </div>

      </div>


      <div class="student-info">

        <div>
          <span>Öğrenci</span>
          <strong>
            ${escapePrintHtml(
              selectedStudent?.label ||
              'Öğrenci'
            )}
          </strong>
        </div>

        <div>
          <span>Sınav</span>
          <strong>
            ${escapePrintHtml(
              getPrintExamLabel()
            )}
          </strong>
        </div>

        <div>
          <span>Günlük Hedef</span>
          <strong>
            ${escapePrintHtml(
              config.dailyMinutes
            )} dk
          </strong>
        </div>

        <div>
          <span>Strateji</span>
          <strong>
            ${escapePrintHtml(
              getPrintStrategyLabel()
            )}
          </strong>
        </div>

      </div>


      <div class="days-grid">

        ${
          days.map(
            day => `

              <div class="day-card">

                <div class="day-header">

                  <div>
                    <h2>
                      ${escapePrintHtml(
                        day?.day
                      )}
                    </h2>

                    <small>
                      ${escapePrintHtml(
                        day?.taskCount || 0
                      )} görev
                    </small>
                  </div>

                  <div class="day-time">
                    ${escapePrintHtml(
                      day?.totalMinutes || 0
                    )}
                    /
                    ${escapePrintHtml(
                      config.dailyMinutes
                    )}
                    dk
                  </div>

                </div>


                <div class="tasks">

                  ${
                    Array.isArray(
                      day?.tasks
                    ) &&
                    day.tasks.length > 0

                      ? day.tasks
                          .map(
                            buildPrintableTask
                          )
                          .join('')

                      : `
                        <div class="empty-day">
                          Bu gün için görev yok.
                        </div>
                      `
                  }

                </div>

              </div>

            `
          ).join('')
        }

      </div>


      <div class="print-footer">
        Rehber360 • Öğrenci Çalışma Planı
      </div>

    </section>
  `;
};


const handlePrintSmartPlan =
  weekNumber => {

        const allWeeks =
      Array.isArray(editableSchedule)
        ? editableSchedule
        : [];


   const hasSpecificWeek =
  weekNumber !== null &&
  weekNumber !== undefined &&
  weekNumber !== '';


const weeksToPrint =
  hasSpecificWeek
    ? allWeeks.filter(
        week =>
          Number(
            week?.weekNumber
          ) ===
          Number(
            weekNumber
          )
      )
    : allWeeks;


    if (
      weeksToPrint.length === 0
    ) {
      alert(
        'Yazdırılabilecek plan bulunamadı.'
      );

      return;
    }


    const iframe =
      document.createElement(
        'iframe'
      );


    iframe.style.position =
      'fixed';

    iframe.style.right =
      '0';

    iframe.style.bottom =
      '0';

    iframe.style.width =
      '0';

    iframe.style.height =
      '0';

    iframe.style.border =
      '0';


    document.body.appendChild(
      iframe
    );


    const printDocument =
      iframe.contentWindow
        ?.document;


    if (!printDocument) {

      document.body.removeChild(
        iframe
      );

      return;
    }


    printDocument.open();


    printDocument.write(`
      <!DOCTYPE html>

      <html lang="tr">

        <head>

          <meta charset="UTF-8" />

          <title>
            Rehber360 Akıllı Çalışma Planı
          </title>

          <style>

            * {
              box-sizing: border-box;
            }

            @page {
              size: A4 landscape;
              margin: 8mm;
            }

            body {
              margin: 0;
              background: #ffffff;
              color: #0f172a;
              font-family:
                Arial,
                Helvetica,
                sans-serif;

              -webkit-print-color-adjust:
                exact;

              print-color-adjust:
                exact;
            }

            .week-page {
  width: 100%;

  page-break-after: always;
  break-after: page;

  page-break-inside: auto;
  break-inside: auto;

  position: relative;

  min-height: 0;
  padding-bottom: 0;
}

            .week-page:last-child {
              page-break-after: auto;
              break-after: auto;
            }

            .week-title {
              display: flex;
              align-items: center;
              justify-content: space-between;
              background: #0f172a;
              color: white;
              padding: 12px 16px;
              border-radius: 10px;
            }

            .week-title h1 {
              margin: 3px 0 0 0;
              font-size: 20px;
            }

            .brand-small {
              font-size: 8px;
              font-weight: 800;
              letter-spacing: 1.4px;
              color: #c7d2fe;
            }

            .week-day-count {
              font-size: 10px;
              font-weight: 700;
              color: #cbd5e1;
            }

            .student-info {
              display: grid;
              grid-template-columns:
                repeat(4, 1fr);
              gap: 8px;
              margin: 10px 0;
            }

            .student-info > div {
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              padding: 7px 9px;
              background: #f8fafc;
            }

            .student-info span {
              display: block;
              color: #64748b;
              font-size: 8px;
              margin-bottom: 3px;
            }

            .student-info strong {
              display: block;
              font-size: 10px;
            }

                       .days-grid {
  display: grid;

  grid-template-columns:
    repeat(3, minmax(0, 1fr));

  gap: 8px;

  align-items: start;

  page-break-inside: auto;
  break-inside: auto;
}


            /* =========================================
               GÜN SAYISINA DUYARLI PRINT GRID
               ========================================= */

            .week-page.print-days-1 .days-grid {
              grid-template-columns:
                minmax(0, 1fr);
            }


            .week-page.print-days-2 .days-grid {
              grid-template-columns:
                repeat(2, minmax(0, 1fr));
            }


            .week-page.print-days-3 .days-grid {
              grid-template-columns:
                repeat(3, minmax(0, 1fr));
            }


            /* 4 gün → 2 + 2 */
            .week-page.print-days-4 .days-grid {
              grid-template-columns:
                repeat(2, minmax(0, 1fr));
            }


            /* -----------------------------------------
               5 gün → 3 + 2

               6 kolonluk görünmez bir grid kullanıyoruz.

               İlk üç kart:
               2 + 2 + 2 = tam satır

               Son iki kart:
               3 + 3 = tam satır
               ----------------------------------------- */

            .week-page.print-days-5 .days-grid {
              grid-template-columns:
                repeat(6, minmax(0, 1fr));
            }

            .week-page.print-days-5 .day-card:nth-child(-n + 3) {
              grid-column:
                span 2;
            }

            .week-page.print-days-5 .day-card:nth-child(n + 4) {
              grid-column:
                span 3;
            }


                       /* 6 gün → 3 + 3 */
            .week-page.print-days-6 .days-grid {
              grid-template-columns:
                repeat(3, minmax(0, 1fr));
            }


            /* -----------------------------------------
               7 gün → 4 + 3

               12 kolonluk görünmez bir grid:

               İlk dört kart:
               3 + 3 + 3 + 3 = tam satır

               Son üç kart:
               4 + 4 + 4 = tam satır

               Böylece üçüncü satır oluşmadan
               bütün hafta iki satırda tamamlanır.
               ----------------------------------------- */

            .week-page.print-days-7 .days-grid {
              grid-template-columns:
                repeat(12, minmax(0, 1fr));
            }

            .week-page.print-days-7 .day-card:nth-child(-n + 4) {
              grid-column:
                span 3;
            }

            .week-page.print-days-7 .day-card:nth-child(n + 5) {
              grid-column:
                span 4;
            }


            .day-card {
              border: 1px solid #cbd5e1;
              border-radius: 10px;
              padding: 9px;
              background: white;
              break-inside: avoid;
            }

            .day-header {
              display: flex;
              align-items: flex-start;
              justify-content: space-between;
              border-bottom: 1px solid #e2e8f0;
              padding-bottom: 7px;
              margin-bottom: 7px;
            }

            .day-header h2 {
              margin: 0;
              font-size: 11px;
            }

            .day-header small {
              color: #94a3b8;
              font-size: 8px;
            }

            .day-time {
              background: #f1f5f9;
              color: #475569;
              border-radius: 999px;
              padding: 4px 7px;
              font-size: 8px;
              font-weight: 800;
            }

            .tasks {
              display: grid;
              gap: 6px;
            }

            .task {
              border:
                1px solid #dbeafe;

              border-left:
                4px solid #6366f1;

              background: #f8fafc;
              border-radius: 8px;
              padding: 7px 8px;
              break-inside: avoid;
            }

            .task-practice {
              border-left-color: #2563eb;
            }

            .task-review {
              border-left-color: #7c3aed;
            }

                       .task-mixed_practice {
              border-left-color: #0891b2;
            }

            .task-topic_scan {
              border-left-color: #0f766e;
            }

            .task-weekly_scan {
              border-left-color: #0284c7;
            }

            .task-branch_mock {
              border-left-color: #d97706;
            }

            .task-full_mock {
              border-left-color: #ea580c;
            }

            .task-mock_analysis {
              border-left-color: #e11d48;
            }

            .task-buffer {
              border-left-color: #64748b;
            }

            .task-routine {
              border-left-color: #059669;
            }

            .task-top {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 6px;
              font-size: 8px;
              font-weight: 800;
              text-transform: uppercase;
            }

            .task-type {
              display: flex;
              align-items: center;
              gap: 5px;
            }

            .checkbox {
              font-size: 13px;
              line-height: 1;
            }

            .task-subject {
              margin-top: 5px;
              font-size: 10px;
              font-weight: 800;
            }

                        .task-topic {
              margin-top: 3px;
              font-size: 9px;
              font-weight: 700;
              color: #475569;
            }

            .task-assessment-scope {
              margin-top: 5px;
              padding: 5px 6px;
              border: 1px solid #dbeafe;
              border-radius: 6px;
              background: #ffffff;
            }

            .task-assessment-title {
              font-size: 7px;
              font-weight: 800;
              text-transform: uppercase;
              letter-spacing: 0.03em;
              color: #64748b;
              margin-bottom: 3px;
            }

            .task-assessment-group {
              margin-top: 2px;
              line-height: 1.3;
            }

            .task-assessment-group strong {
              display: block;
              font-size: 7.5px;
              color: #334155;
            }

            .task-assessment-group span {
              display: block;
              margin-top: 1px;
              font-size: 7px;
              color: #64748b;
            }

            .task-description {
              margin-top: 4px;
              font-size: 7.5px;
              line-height: 1.35;
              color: #64748b;
            }

            .task-meta {
              margin-top: 4px;
              font-size: 7.5px;
              color: #64748b;
            }

            .empty-day {
              color: #94a3b8;
              font-size: 9px;
              font-style: italic;
              padding: 7px;
            }

           .print-footer {
  position: static;

  margin-top: 6px;

  padding-top: 4px;

  border-top:
    1px solid #e2e8f0;

  text-align: center;

  color: #94a3b8;

  font-size: 7px;
}


                /* =========================================
               COMPACT PRINT DENSITY
               ========================================= */

            .week-page.print-density-compact .week-title {
              padding: 9px 13px;
              border-radius: 8px;
            }

            .week-page.print-density-compact .week-title h1 {
              font-size: 18px;
              margin-top: 2px;
            }

            .week-page.print-density-compact .student-info {
              gap: 6px;
              margin: 7px 0;
            }

            .week-page.print-density-compact .student-info > div {
              padding: 5px 7px;
              border-radius: 7px;
            }

            .week-page.print-density-compact .student-info span {
              font-size: 7px;
              margin-bottom: 2px;
            }

            .week-page.print-density-compact .student-info strong {
              font-size: 9px;
            }

            .week-page.print-density-compact .days-grid {
              gap: 6px;
            }

            .week-page.print-density-compact .day-card {
              padding: 7px;
              border-radius: 8px;
            }

            .week-page.print-density-compact .day-header {
              padding-bottom: 5px;
              margin-bottom: 5px;
            }

            .week-page.print-density-compact .day-header h2 {
              font-size: 10px;
            }

            .week-page.print-density-compact .day-header small {
              font-size: 7px;
            }

            .week-page.print-density-compact .day-time {
              padding: 3px 6px;
              font-size: 7px;
            }

            .week-page.print-density-compact .tasks {
              gap: 4px;
            }

            .week-page.print-density-compact .task {
              padding: 5px 6px;
              border-radius: 7px;
            }

            .week-page.print-density-compact .task-top {
              font-size: 7px;
              gap: 4px;
            }

            .week-page.print-density-compact .checkbox {
              font-size: 11px;
            }

            .week-page.print-density-compact .task-subject {
              margin-top: 3px;
              font-size: 9px;
            }

            .week-page.print-density-compact .task-topic {
              margin-top: 2px;
              font-size: 8px;
            }

            .week-page.print-density-compact .task-description {
              margin-top: 3px;
              font-size: 6.7px;
              line-height: 1.25;
            }

            .week-page.print-density-compact .task-meta {
              margin-top: 3px;
              font-size: 6.7px;
            }

            .week-page.print-density-compact .task-assessment-scope {
              margin-top: 4px;
              padding: 4px 5px;
            }

            .week-page.print-density-compact .task-assessment-title {
              font-size: 6.5px;
              margin-bottom: 2px;
            }

            .week-page.print-density-compact .task-assessment-group {
              margin-top: 1px;
              line-height: 1.2;
            }

            .week-page.print-density-compact .task-assessment-group strong {
              font-size: 7px;
            }

            .week-page.print-density-compact .task-assessment-group span {
              font-size: 6.5px;
            }


            /* =========================================
               DENSE PRINT DENSITY
               ========================================= */

            .week-page.print-density-dense .week-title {
              padding: 8px 12px;
              border-radius: 8px;
            }

            .week-page.print-density-dense .week-title h1 {
              font-size: 17px;
              margin-top: 1px;
            }

            .week-page.print-density-dense .brand-small {
              font-size: 7px;
            }

            .week-page.print-density-dense .student-info {
              gap: 5px;
              margin: 6px 0;
            }

            .week-page.print-density-dense .student-info > div {
              padding: 4px 6px;
              border-radius: 6px;
            }

            .week-page.print-density-dense .student-info span {
              font-size: 6.5px;
              margin-bottom: 1px;
            }

            .week-page.print-density-dense .student-info strong {
              font-size: 8.5px;
            }

            .week-page.print-density-dense .days-grid {
              gap: 5px;
            }

            .week-page.print-density-dense .day-card {
              padding: 6px;
              border-radius: 7px;
            }

            .week-page.print-density-dense .day-header {
              padding-bottom: 4px;
              margin-bottom: 4px;
            }

            .week-page.print-density-dense .day-header h2 {
              font-size: 9px;
            }

            .week-page.print-density-dense .day-header small {
              font-size: 6.5px;
            }

            .week-page.print-density-dense .day-time {
              padding: 2px 5px;
              font-size: 6.5px;
            }

            .week-page.print-density-dense .tasks {
              gap: 3px;
            }

            .week-page.print-density-dense .task {
              padding: 4px 5px;
              border-radius: 6px;
              border-left-width: 3px;
            }

            .week-page.print-density-dense .task-top {
              font-size: 6.7px;
              gap: 3px;
            }

            .week-page.print-density-dense .checkbox {
              font-size: 10px;
            }

            .week-page.print-density-dense .task-subject {
              margin-top: 2px;
              font-size: 8.5px;
            }

            .week-page.print-density-dense .task-topic {
              margin-top: 1px;
              font-size: 7.3px;
            }

            /*
             * Çok yoğun haftada genel açıklama,
             * konu / görev / süre bilgisinden daha düşük
             * önceliklidir.
             *
             * Böylece veri kaybetmeden dikey alan kazanırız.
             */
            .week-page.print-density-dense .task-description {
              display: none;
            }

            .week-page.print-density-dense .task-meta {
              margin-top: 2px;
              font-size: 6.2px;
            }

            .week-page.print-density-dense .task-assessment-scope {
              margin-top: 3px;
              padding: 3px 4px;
              border-radius: 5px;
            }

            .week-page.print-density-dense .task-assessment-title {
              font-size: 6px;
              margin-bottom: 1px;
            }

            .week-page.print-density-dense .task-assessment-group {
              margin-top: 1px;
              line-height: 1.15;
            }

            .week-page.print-density-dense .task-assessment-group strong {
              font-size: 6.5px;
            }

            .week-page.print-density-dense .task-assessment-group span {
              font-size: 6px;
            }

          </style>

        </head>

        <body>

          ${
            weeksToPrint
              .map(
                buildPrintableWeek
              )
              .join('')
          }

        </body>

      </html>
    `);


    printDocument.close();


    setTimeout(
      () => {

        iframe.contentWindow
          ?.focus();


        iframe.contentWindow
          ?.print();


        setTimeout(
          () => {

            if (
              document.body.contains(
                iframe
              )
            ) {
              document.body.removeChild(
                iframe
              );
            }

          },
          1000
        );

      },
      350
    );
  };





// =========================================================
// AKILLI PLAN — ÖĞRENCİYE DİJİTAL ATAMA
// =========================================================

const handleAssignSmartPlan =
  async () => {

       if (
      !resultReady ||
      !selectedStudent ||
      !Array.isArray(
        editableSchedule
      ) ||
      editableSchedule.length === 0
    ) {
      alert(
        'Öğrenciye atanabilecek hazır bir plan bulunamadı.'
      );

      return;
    }


      if (
      typeof onAssign !==
      'function'
    ) {
      alert(
        'Plan kayıt bağlantısı kullanılamıyor.'
      );

      return;
    }


    setAssignWarningConfirmOpen(
      false
    );


    // =========================================
    // STANDART PAKET KOÇLUK LİMİTİ
    // =========================================

    if (!user?.isPremium) {

      const coachingModules = [
        'gunluk_soru_takibi',
        'deneme_net_takibi',
        'weekly_plan',
        'ai_macro_plan',
        'smart_plan'
      ];


      const activeStudents =
        [
          ...new Set(
            submissions
              .filter(
                submission =>
                  coachingModules.includes(
                    submission?.testId
                  )
              )
              .map(
                submission =>
                  String(
                    submission?.studentNo
                  )
              )
          )
        ];


      const studentAlreadyActive =
        activeStudents.includes(
          String(
            selectedStudent.no
          )
        );


      if (
        !studentAlreadyActive &&
        activeStudents.length >= 5
      ) {
        alert(
          '⚠️ STANDART PAKET LİMİTİ\n\n' +
          'Ücretsiz sürümde koçluk modülüne en fazla 5 öğrenci ekleyebilirsiniz.\n\n' +
          'Sınırsız öğrenci takibi için Premium üyelik gereklidir.'
        );

        return;
      }
    }


    setIsAssigning(true);
    setAssignSuccess(false);


    try {

      const createdAt =
        Date.now();


      const planData = {

        id:
          createdAt,

        createdAt,

        academicYear,

        studentClass:
          'Koçluk',

        targetClass:
          selectedStudent
            .className ||
          '',

        studentNo:
          String(
            selectedStudent.no
          ),

        studentName:
          selectedStudent
            .name ||
          selectedStudent
            .label ||
          '',

        clinicianCode:
          user?.refCode ||
          '',

        testId:
          'smart_plan',

        testName:
          'Akıllı Çalışma Planı',

        answers: {

          planType:
            'smart_plan',

          examMode:
  config.examMode,

curriculumModel:
  config.curriculumModel ||
  MUFREDAT_MODELLERI.MEVCUT,

field:
  config.field ||
  null,

          strategyId:
  config.strategyId,

studentLevel:
  config.studentLevel,

difficulty:
  config.difficulty,

weeks:
  config.weeks,

                   dailyMinutes:
            config.dailyMinutes,

          maxSubjectsPerDay:
            config.maxSubjectsPerDay ??
            null,

          workingDays:
            [
              ...config.workingDays
            ],

          selectedSubjects:
            [
              ...config.selectedSubjects
            ],

          subjectPriorities:
            {
              ...config.subjectPriorities
            },

          selectedTopics:
            {
              ...config.selectedTopics
            },

                   topicStatuses:
            {
              ...config.topicStatuses
            },

          resourcePreferences:
            {
              ...(
                config.resourcePreferences ||
                {}
              )
            },

          schedule:
            editableSchedule,

          scheduledSlots:
            result.scheduledSlots ||
            [],

          createdWith:
            'smart_plan_robot'
        },

        status:
          'assigned'
      };


      // Firestore "undefined" kabul etmez.
      // Motor çıktısını güvenli JSON nesnesine çeviriyoruz.
      const safePlanData =
        JSON.parse(
          JSON.stringify(
            planData
          )
        );


     const saveSuccess =
  await onAssign(
    safePlanData
  );


if (saveSuccess !== true) {

  setAssignSuccess(false);

  alert(
    '⚠️ Akıllı plan oluşturuldu ancak öğrenciye kaydedilemedi.\n\n' +
    'İnternet bağlantısını kontrol edip tekrar deneyiniz.'
  );

  return;
}


setAssignSuccess(true);


alert(
  `✅ Akıllı plan ${selectedStudent.name || selectedStudent.label} için dijital olarak atandı.`
);

    } catch (error) {

      console.error(
        'Akıllı Plan atama hatası:',
        error
      );


      alert(
        '⚠️ Plan öğrenciye atanırken bir hata oluştu.'
      );

        } finally {

      setIsAssigning(false);

    }
  };


  const handleAssignRequest = () => {

    if (
      livePlanWarnings.length > 0
    ) {

      setAssignWarningConfirmOpen(
        true
      );

      return;
    }


    handleAssignSmartPlan();
  };


  return (
    <div className="mb-8 rounded-3xl border border-indigo-100 bg-white shadow-sm overflow-hidden">

      <div className="bg-gradient-to-r from-slate-950 via-indigo-950 to-slate-900 px-6 py-6 text-white">

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">

          <div className="flex items-start gap-4">

            <div className="h-12 w-12 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-center shrink-0">
              <Brain className="h-6 w-6 text-indigo-200" />
            </div>

            <div>

              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-300">
                Rehber360 Akıllı Plan Robotu
              </p>

              <h2 className="text-2xl font-black mt-1">
                Öğrenciye uygulanabilir çalışma planı üretin
              </h2>

              <p className="text-sm text-slate-300 mt-2 max-w-3xl leading-relaxed">
                Günlük süreyi hard limit kabul eder; seçilen ders,
                konu, öncelik ve stratejiye göre planı gerçek
                çalışma bloklarına dönüştürür.
              </p>

            </div>

          </div>


          <button
            type="button"
            onClick={resetBuilder}
            className="self-start lg:self-center px-4 py-2.5 rounded-xl bg-white/10 border border-white/10 hover:bg-white/15 transition text-xs font-black flex items-center gap-2"
          >
            <RotateCcw className="h-4 w-4" />

            Sıfırla
          </button>

        </div>

      </div>


      <div className="px-5 md:px-6 py-4 border-b border-slate-100 bg-slate-50/70 overflow-x-auto">

        <div className="flex min-w-[680px] items-center gap-2">

          {STEPS.map(
            (
              label,
              index
            ) => (

              <React.Fragment
                key={label}
              >

                <div className="flex items-center gap-2 shrink-0">

                  <div
                    className={`h-7 w-7 rounded-full flex items-center justify-center text-[11px] font-black ${
                      index < step
                        ? 'bg-emerald-500 text-white'
                        : index === step
                          ? 'bg-indigo-600 text-white'
                          : 'bg-white border border-slate-200 text-slate-400'
                    }`}
                  >

                    {
                      index < step
                        ? (
                          <CheckCircle2 className="h-4 w-4" />
                        )
                        : index + 1
                    }

                  </div>


                  <span
                    className={`text-xs font-black ${
                      index === step
                        ? 'text-indigo-700'
                        : 'text-slate-500'
                    }`}
                  >
                    {label}
                  </span>

                </div>


                {
                  index <
                  STEPS.length - 1 &&
                  (
                    <div className="h-px flex-1 min-w-5 bg-slate-200" />
                  )
                }

              </React.Fragment>

            )
          )}

        </div>

      </div>


      <div className="p-5 md:p-7">

        {step === 0 && (

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            <div className="space-y-5">

              <div>

                <label className="block text-xs font-black text-slate-700 mb-2">
                  Öğrenci
                </label>

                <select
                  value={studentKey}
                  onChange={
                    e =>
                      setStudentKey(
                        e.target.value
                      )
                  }
                  className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 bg-white font-bold text-sm text-slate-700 focus:outline-none focus:border-indigo-500"
                >

                  <option value="">
                    Öğrenci seçin...
                  </option>

                  {students.map(
                    student => (

                      <option
                        key={`${student.className}-${student.no}`}
                        value={`${student.className}__${student.no}`}
                      >
                        {student.label}
                      </option>

                    )
                  )}

                </select>

              </div>


              <div>

                <label className="block text-xs font-black text-slate-700 mb-2">
                  Sınav türü
                </label>

                <div className="grid grid-cols-2 gap-2">

                  {EXAM_OPTIONS.map(
                    option => (

                      <button
                        key={
                          option.value
                        }
                        type="button"
                        onClick={
                          () =>
                            handleExamModeChange(
                              option.value
                            )
                        }
                        className={`rounded-xl border-2 px-4 py-3 text-sm font-black transition ${
                          config.examMode ===
                          option.value
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                            : 'border-slate-200 bg-white text-slate-600 hover:border-indigo-200'
                        }`}
                      >
                        {option.label}
                      </button>

                    )
                  )}

                                </div>

              </div>


              {config.examMode !==
                SMART_PLAN_EXAM_MODES.LGS && (

                <div>

                  <label className="block text-xs font-black text-slate-700 mb-2">
                    Müfredat
                  </label>


                  <div className="grid grid-cols-1 gap-2">

                    {CURRICULUM_OPTIONS.map(
                      option => {

                        const active =
                          config.curriculumModel ===
                          option.value;


                        return (

                          <button
                            key={option.value}
                            type="button"
                            onClick={
                              () =>
                                handleCurriculumModelChange(
                                  option.value
                                )
                            }
                            className={`rounded-xl border-2 px-4 py-3 text-left transition ${
                              active
                                ? 'border-emerald-500 bg-emerald-50'
                                : 'border-slate-200 bg-white hover:border-emerald-200'
                            }`}
                          >

                            <div
                              className={`text-sm font-black ${
                                active
                                  ? 'text-emerald-800'
                                  : 'text-slate-700'
                              }`}
                            >
                              {option.label}
                            </div>


                            <div className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                              {option.description}
                            </div>

                          </button>
                        );
                      }
                    )}

                  </div>


                 

                </div>
              )}

            </div>


            <div className="space-y-5">

              {needsField && (

                <div>

                  <label className="block text-xs font-black text-slate-700 mb-2">
                    Alan
                  </label>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">

                    {FIELD_OPTIONS.map(
                      option => (

                        <button
                          key={
                            option.value
                          }
                          type="button"
                          onClick={
                            () =>
                              handleFieldChange(
                                option.value
                              )
                          }
                          className={`rounded-xl border-2 px-3 py-3 text-xs font-black transition ${
                            config.field ===
                            option.value
                              ? 'border-violet-500 bg-violet-50 text-violet-700'
                              : 'border-slate-200 bg-white text-slate-600 hover:border-violet-200'
                          }`}
                        >
                          {option.label}
                        </button>

                      )
                    )}

                  </div>

                </div>

              )}


              <div>

                <label className="block text-xs font-black text-slate-700 mb-2">
  Plan süresi
</label>


<button
  type="button"
  onClick={
    () => {

      setConfig(
        prev => ({
          ...prev,

          weeks: 1
        })
      );

      clearPlanResult();
    }
  }
  className={`w-full mb-3 rounded-2xl border-2 px-4 py-3.5 text-left transition ${
    Number(config.weeks) === 1
      ? 'border-amber-400 bg-amber-50 shadow-sm'
      : 'border-slate-200 bg-white hover:border-amber-300 hover:bg-amber-50/50'
  }`}
>
  <div className="flex items-center justify-between gap-3">

    <div>

      <div className="flex items-center gap-2">

        <span className="text-lg">
          ⚡
        </span>

        <span
          className={`text-sm font-black ${
            Number(config.weeks) === 1
              ? 'text-amber-800'
              : 'text-slate-800'
          }`}
        >
          1 Haftalık Hızlı Plan
        </span>

      </div>

      <p className="text-[11px] text-slate-500 mt-1">
        Seçtiğiniz ders ve konularla yalnızca bu haftaya odaklanan çalışma programı oluşturur.
      </p>

    </div>


    <div
      className={`shrink-0 px-3 py-1.5 rounded-full text-[10px] font-black ${
        Number(config.weeks) === 1
          ? 'bg-amber-500 text-white'
          : 'bg-slate-100 text-slate-500'
      }`}
    >
      1 HAFTA
    </div>

  </div>
</button>


<div className="flex items-center gap-3">

  <input
    type="number"
    min="1"
    max="36"
    value={
      config.weeks
    }
                    onChange={
                      e => {

                        setConfig(
                          prev => ({
                            ...prev,

                            weeks:
                              Number(
                                e.target.value
                              )
                          })
                        );

                        clearPlanResult();
                      }
                    }
                    className="w-28 px-4 py-3 rounded-xl border-2 border-slate-200 font-black text-indigo-700 focus:outline-none focus:border-indigo-500"
                  />

                  <span className="text-sm font-bold text-slate-500">
                    hafta
                  </span>

                </div>


                <p className="text-xs text-slate-400 mt-2">
                  1–36 hafta arasında plan oluşturabilirsiniz.
                </p>

              </div>


              {selectedStudent && (

                <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">

                  <p className="text-[10px] font-black uppercase tracking-wider text-emerald-600">
                    Plan öğrencisi
                  </p>

                  <p className="text-sm font-black text-emerald-900 mt-1">
                    {selectedStudent.label}
                  </p>

                </div>

              )}

            </div>

          </div>

        )}


        {step === 1 && (

          <div className="space-y-7">

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

              <div className="rounded-2xl border border-slate-200 p-5">

                <div className="flex items-center gap-3 mb-4">

                  <Clock3 className="h-5 w-5 text-indigo-600" />

                  <div>

                    <h3 className="text-sm font-black text-slate-900">
                      Günlük çalışma süresi
                    </h3>

                    <p className="text-xs text-slate-500 mt-0.5">
                      Robot bu değeri kesin üst sınır olarak kabul eder.
                    </p>

                  </div>

                </div>


                <div className="flex items-center gap-3">

                  <input
                    type="number"
                    min="30"
                    max="600"
                    step="15"
                    value={
                      config.dailyMinutes
                    }
                    onChange={
                      e => {

                        setConfig(
                          prev => ({
                            ...prev,

                            dailyMinutes:
                              Number(
                                e.target.value
                              )
                          })
                        );

                        clearPlanResult();
                      }
                    }
                    className="w-32 px-4 py-3 rounded-xl border-2 border-slate-200 font-black text-indigo-700 focus:outline-none focus:border-indigo-500"
                  />

                  <span className="text-sm font-bold text-slate-500">
                    dakika / gün
                  </span>

                </div>

              </div>


              <div className="rounded-2xl border border-slate-200 p-5">

                <div className="flex items-center gap-3 mb-4">

                  <Target className="h-5 w-5 text-violet-600" />

                  <div>

                    <h3 className="text-sm font-black text-slate-900">
                      Öğrenci seviyesi
                    </h3>

                    <p className="text-xs text-slate-500 mt-0.5">
                      İlk sürümde genel zorluk tercihiyle birlikte kullanılır.
                    </p>

                  </div>

                </div>


                <div className="grid grid-cols-3 gap-2">

                  {[
                    'Kolay',
                    'Orta',
                    'Zor'
                  ].map(
                    level => (

                      <button
                        key={level}
                        type="button"
                        onClick={
                          () =>
                            setConfig(
                              prev => ({
                                ...prev,

                                studentLevel:
                                  level,

                                difficulty:
                                  level
                              })
                            )
                        }
                        className={`rounded-xl border-2 py-2.5 text-xs font-black ${
                          config.studentLevel ===
                          level
                            ? 'border-violet-500 bg-violet-50 text-violet-700'
                            : 'border-slate-200 text-slate-600'
                        }`}
                      >
                        {level}
                      </button>

                    )
                  )}

                </div>

              </div>

            </div>




            <div className="rounded-2xl border border-slate-200 p-5">

              <div className="flex items-start gap-3 mb-4">

                <BookOpen className="h-5 w-5 text-cyan-600 mt-0.5" />

                <div>

                  <h3 className="text-sm font-black text-slate-900">
                    Günlük ders çeşitliliği
                  </h3>

                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                    Bir çalışma gününde en fazla kaç farklı ders bulunabileceğini belirleyin.
                    Görev sayısını değil, farklı ders sayısını sınırlar.
                  </p>

                </div>

              </div>


              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">

                {DAILY_SUBJECT_LIMIT_OPTIONS.map(
                  option => {

                    const active =
                      option.value === null
                        ? config.maxSubjectsPerDay == null
                        : Number(
                            config.maxSubjectsPerDay
                          ) === option.value;


                    return (

                      <button
                        key={
                          option.value === null
                            ? 'auto'
                            : option.value
                        }
                        type="button"
                        onClick={
                          () => {

                            setConfig(
                              prev => ({
                                ...prev,

                                maxSubjectsPerDay:
                                  option.value
                              })
                            );

                            clearPlanResult();
                          }
                        }
                        className={`rounded-xl border-2 px-3 py-3 text-left transition ${
                          active
                            ? 'border-cyan-500 bg-cyan-50 shadow-sm'
                            : 'border-slate-200 bg-white hover:border-cyan-200 hover:bg-cyan-50/40'
                        }`}
                      >

                        <div
                          className={`text-xs font-black ${
                            active
                              ? 'text-cyan-800'
                              : 'text-slate-700'
                          }`}
                        >
                          {option.label}
                        </div>

                        <p className="text-[10px] text-slate-500 mt-1 leading-relaxed">
                          {option.description}
                        </p>

                      </button>

                    );
                  }
                )}

              </div>


              <div className="mt-3 rounded-xl bg-slate-50 border border-slate-100 px-3 py-2.5">

                <p className="text-[10px] text-slate-500 leading-relaxed">

                  <strong className="text-slate-700">
                    Örnek:
                  </strong>{' '}

                  Matematikten iki görev ve Türkçeden bir görev aynı güne gelirse,
                  bu 3 görev fakat 2 farklı ders olarak değerlendirilir.

                </p>

              </div>

            </div>


            <div>

              <div className="flex items-center gap-3 mb-3">

                <CalendarDays className="h-5 w-5 text-emerald-600" />

                <div>

                  <h3 className="text-sm font-black text-slate-900">
                    Çalışma günleri
                  </h3>

                  <p className="text-xs text-slate-500">
                    Öğrencinin gerçekçi biçimde çalışabileceği günleri seçin.
                  </p>

                </div>

              </div>


              <div className="flex flex-wrap gap-2">

                {DAYS.map(
                  day => {

                    const active =
                      config.workingDays.includes(
                        day
                      );

                    return (

                      <button
                        key={day}
                        type="button"
                        onClick={
                          () =>
                            toggleWorkingDay(
                              day
                            )
                        }
                        className={`px-4 py-2.5 rounded-xl border-2 text-xs font-black transition ${
                          active
                            ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                            : 'border-slate-200 bg-white text-slate-500'
                        }`}
                      >
                        {day}
                      </button>

                    );
                  }
                )}

              </div>

            </div>

          </div>

        )}


        {step === 2 && (

          <div className="space-y-6">

            {
              needsField &&
              !config.field
                ? (

                  <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm font-bold text-amber-800">
                    Dersleri görebilmek için önce alan seçimini tamamlayın.
                  </div>

                )
                : (

                  <>

                    <div>

                      <h3 className="text-base font-black text-slate-900">
                        Dersleri seçin
                      </h3>

                      <p className="text-xs text-slate-500 mt-1">
                        Ders seçildikten sonra önceliğini ve isterseniz
                        çalışılacak konu havuzunu daraltabilirsiniz.
                      </p>

                    </div>


                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">

                      {allowedSubjects.map(
                        subject => {

                          const active =
                            config
                              .selectedSubjects
                              .includes(
                                subject
                              );

                          return (

                            <div
                              key={subject}
                              className={`rounded-2xl border-2 p-4 ${
                                active
                                  ? 'border-indigo-300 bg-indigo-50/50'
                                  : 'border-slate-200 bg-white'
                              }`}
                            >

                              <button
                                type="button"
                                onClick={
                                  () =>
                                    toggleSubject(
                                      subject
                                    )
                                }
                                className="w-full text-left flex items-start gap-3"
                              >

                                <div
                                  className={`h-5 w-5 rounded-md border-2 mt-0.5 flex items-center justify-center shrink-0 ${
                                    active
                                      ? 'bg-indigo-600 border-indigo-600 text-white'
                                      : 'border-slate-300'
                                  }`}
                                >
                                  {
                                    active &&
                                    (
                                      <CheckCircle2 className="h-3.5 w-3.5" />
                                    )
                                  }
                                </div>


                                <div>

                                  <p className="text-sm font-black text-slate-800">
                                    {subject}
                                  </p>

                                  <p className="text-[11px] text-slate-400 mt-1">
                                    {
                                      (
  activeMufredat?.[
    subject
  ] || []
).length
                                    } konu
                                  </p>

                                </div>

                              </button>


                              {active && (

                                <div className="mt-4 pt-4 border-t border-indigo-100 space-y-3">

                                  <select
                                    value={
                                      config
                                        .subjectPriorities
                                        ?.[subject] ||
                                      SMART_PLAN_PRIORITY_LEVELS.NORMAL
                                    }
                                    onChange={
                                      e =>
                                        setSubjectPriority(
                                          subject,
                                          e.target.value
                                        )
                                    }
                                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-700"
                                  >

                                    {
                                      PRIORITY_OPTIONS.map(
                                        option => (

                                          <option
                                            key={
                                              option.value
                                            }
                                            value={
                                              option.value
                                            }
                                          >
                                            {
                                              option.label
                                            }
                                          </option>

                                        )
                                      )
                                    }

                                  </select>


                                  <button
                                    type="button"
                                    onClick={
                                      () =>
                                        setOpenSubject(
                                          openSubject ===
                                          subject
                                            ? ''
                                            : subject
                                        )
                                    }
                                    className="w-full px-3 py-2.5 rounded-xl bg-white border border-slate-200 text-xs font-black text-indigo-600 hover:border-indigo-300"
                                  >
                                    {
                                      openSubject ===
                                      subject
                                        ? 'Konu Ayarlarını Kapat'
                                        : 'Konu Ayarlarını Aç'
                                    }
                                  </button>

                                </div>

                              )}

                            </div>

                          );
                        }
                      )}

                    </div>


                    {
                      openSubject &&
                      config
                        .selectedSubjects
                        .includes(
                          openSubject
                        ) &&
                      (

                        <div className="rounded-2xl border border-indigo-200 bg-slate-50 p-4 md:p-5">

                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-4">

                            <div>

                              <h4 className="text-sm font-black text-slate-900">
                                {openSubject} — Konu Havuzu
                              </h4>

                              <p className="text-xs text-slate-500 mt-1">
                                Hiç konu işaretlemezseniz robot tüm müfredatı kullanır.
                                Bir konu işaretlediğiniz anda yalnız işaretlenen konular planlanır.
                              </p>

                            </div>


                            <span className="text-[10px] font-black uppercase tracking-wider text-indigo-600 bg-indigo-50 border border-indigo-100 rounded-full px-3 py-1.5">
                              {
                                (
                                  config
                                    .selectedTopics
                                    ?.[openSubject] ||
                                  []
                                ).length
                              } özel konu seçili
                            </span>

                          </div>


                          <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">

                            {
                              (
  activeMufredat?.[
    openSubject
  ] || []
).map(
                                topic => {

                                  const explicitlySelected =
                                    (
                                      config
                                        .selectedTopics
                                        ?.[openSubject] ||
                                      []
                                    ).includes(
                                      topic
                                    );

                                  const statusKey =
                                    `${openSubject}__${topic}`;

                                  const status =
                                    config
                                      .topicStatuses
                                      ?.[statusKey] ||
                                    SMART_PLAN_TOPIC_STATUS.NOT_STARTED;

                                  return (

                                    <div
                                      key={topic}
                                      className="grid grid-cols-1 md:grid-cols-[1fr_190px] gap-3 items-center rounded-xl border border-slate-200 bg-white p-3"
                                    >

                                      <button
                                        type="button"
                                        onClick={
                                          () =>
                                            toggleTopic(
                                              openSubject,
                                              topic
                                            )
                                        }
                                        className="text-left flex items-center gap-3"
                                      >

                                        <div
                                          className={`h-5 w-5 rounded-md border-2 flex items-center justify-center shrink-0 ${
                                            explicitlySelected
                                              ? 'bg-indigo-600 border-indigo-600 text-white'
                                              : 'border-slate-300'
                                          }`}
                                        >
                                          {
                                            explicitlySelected &&
                                            (
                                              <CheckCircle2 className="h-3.5 w-3.5" />
                                            )
                                          }
                                        </div>


                                        <span className="text-xs font-bold text-slate-700">
                                          {topic}
                                        </span>

                                      </button>


                                      <select
                                        value={status}
                                        onChange={
                                          e =>
                                            setTopicStatus(
                                              openSubject,
                                              topic,
                                              e.target.value
                                            )
                                        }
                                        className="px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-[11px] font-bold text-slate-600"
                                      >

                                        {
                                          TOPIC_STATUS_OPTIONS.map(
                                            option => (

                                              <option
                                                key={
                                                  option.value
                                                }
                                                value={
                                                  option.value
                                                }
                                              >
                                                {
                                                  option.label
                                                }
                                              </option>

                                            )
                                          )
                                        }

                                                                            </select>

                                    </div>

                                  );
                                }
                              )
                            }

                          </div>


                          {
                            Object.entries(
                              kaynaklarHavuzu?.[
                                openSubject
                              ] ||
                              {}
                            ).length > 0 &&
                            (

                              <div className="mt-5 pt-5 border-t border-slate-200">

                                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-4">

                                  <div>

                                    <h4 className="text-sm font-black text-slate-900">
                                      Kaynaklarım
                                    </h4>

                                    <p className="text-xs text-slate-500 mt-1 max-w-2xl">
                                      Öğrencinin elinde bulunan veya kullanmak istediğiniz kaynakları işaretleyin.
                                      Hiç kaynak seçmezseniz mevcut otomatik kaynak önerisi kullanılacaktır.
                                    </p>

                                  </div>


                                  <span className="shrink-0 text-[10px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1.5">

                                    {
                                      (
                                        config
                                          .resourcePreferences
                                          ?.[openSubject] ||
                                        []
                                      ).length
                                    } kaynak seçili

                                  </span>

                                </div>


                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">

                                  {
                                    Object.entries(
                                      kaynaklarHavuzu?.[
                                        openSubject
                                      ] ||
                                      {}
                                    ).map(
                                      ([
                                        difficulty,
                                        resources
                                      ]) => (

                                        <div
                                          key={
                                            difficulty
                                          }
                                          className="rounded-xl border border-slate-200 bg-white p-3"
                                        >

                                          <div className="flex items-center justify-between gap-2 mb-3">

                                            <p className="text-xs font-black text-slate-800">
                                              {difficulty} Seviye
                                            </p>

                                            <span className="text-[9px] font-black text-slate-400 bg-slate-50 border border-slate-100 rounded-full px-2 py-1">
                                              {
                                                Array.isArray(
                                                  resources
                                                )
                                                  ? resources.length
                                                  : 0
                                              } kaynak
                                            </span>

                                          </div>


                                          <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">

                                            {
                                              (
                                                Array.isArray(
                                                  resources
                                                )
                                                  ? resources
                                                  : []
                                              ).map(
                                                resource => {

                                                  const selected =
                                                    (
                                                      config
                                                        .resourcePreferences
                                                        ?.[openSubject] ||
                                                      []
                                                    ).includes(
                                                      resource
                                                    );


                                                  return (

                                                    <button
                                                      key={
                                                        resource
                                                      }
                                                      type="button"
                                                      onClick={
                                                        () =>
                                                          toggleResourcePreference(
                                                            openSubject,
                                                            resource
                                                          )
                                                      }
                                                      className={`w-full text-left flex items-start gap-2.5 rounded-lg border px-2.5 py-2 transition ${
                                                        selected
                                                          ? 'border-emerald-300 bg-emerald-50'
                                                          : 'border-slate-200 bg-white hover:border-emerald-200'
                                                      }`}
                                                    >

                                                      <div
                                                        className={`h-4.5 w-4.5 rounded border-2 mt-0.5 flex items-center justify-center shrink-0 ${
                                                          selected
                                                            ? 'bg-emerald-600 border-emerald-600 text-white'
                                                            : 'border-slate-300'
                                                        }`}
                                                      >

                                                        {
                                                          selected &&
                                                          (
                                                            <CheckCircle2 className="h-3 w-3" />
                                                          )
                                                        }

                                                      </div>


                                                      <span
                                                        className={`text-[11px] leading-relaxed ${
                                                          selected
                                                            ? 'font-black text-emerald-900'
                                                            : 'font-bold text-slate-600'
                                                        }`}
                                                      >
                                                        {resource}
                                                      </span>

                                                    </button>

                                                  );
                                                }
                                              )
                                            }

                                          </div>

                                        </div>

                                      )
                                    )
                                  }

                                </div>

                              </div>

                            )
                          }

                        </div>

                      )
                    }

                  </>

                )
            }

          </div>

        )}


        {step === 3 && (

          <div className="space-y-6">

            <div>

              <h3 className="text-base font-black text-slate-900">
                Çalışma stratejisini seçin
              </h3>

              <p className="text-xs text-slate-500 mt-1">
                Strateji planın karakterini belirler;
                ders ve konuları değiştirmez.
              </p>

            </div>


            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">

              {
                SMART_PLAN_STRATEGY_OPTIONS.map(
                  option => {

                    const active =
                      config.strategyId ===
                      option.value;

                    const strategy =
                      SMART_PLAN_STRATEGIES[
                        option.value
                      ];

                    return (

                      <button
                        key={
                          option.value
                        }
                        type="button"
                        onClick={
                          () => {

                            setConfig(
                              prev => ({
                                ...prev,

                                strategyId:
                                  option.value
                              })
                            );

                            clearPlanResult();
                          }
                        }
                        className={`text-left rounded-2xl border-2 p-5 transition ${
                          active
                            ? 'border-indigo-500 bg-indigo-50 shadow-sm'
                            : 'border-slate-200 bg-white hover:border-indigo-200'
                        }`}
                      >

                        <div className="flex items-start justify-between gap-3">

                          <div
                            className={`h-10 w-10 rounded-xl flex items-center justify-center ${
                              active
                                ? 'bg-indigo-600 text-white'
                                : 'bg-slate-100 text-slate-500'
                            }`}
                          >
                            <Sparkles className="h-5 w-5" />
                          </div>


                          {
                            active &&
                            (
                              <CheckCircle2 className="h-5 w-5 text-indigo-600" />
                            )
                          }

                        </div>


                        <h4 className="text-sm font-black text-slate-900 mt-4">
                          {option.label}
                        </h4>

                        <p className="text-xs text-slate-500 leading-relaxed mt-2">
                          {option.description}
                        </p>

                        <div className="mt-4 text-[10px] font-black text-slate-400 uppercase tracking-wider">
                          Ağır görev / gün: {
                            strategy
                              ?.maxHeavyTasksPerDay ||
                            1
                          }
                        </div>

                      </button>

                    );
                  }
                )
              }

            </div>


            <div className="rounded-2xl border border-indigo-100 bg-indigo-50 p-5">

              <p className="text-[10px] uppercase tracking-wider font-black text-indigo-500">
                Plan özeti
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-xs">

                <div>

                  <span className="text-slate-500">
                    Öğrenci
                  </span>

                  <p className="font-black text-slate-800 mt-1">
                    {
                      selectedStudent
                        ?.label ||
                      '-'
                    }
                  </p>

                </div>


                <div>

                  <span className="text-slate-500">
                    Süre
                  </span>

                  <p className="font-black text-slate-800 mt-1">
                    {config.weeks} hafta
                  </p>

                </div>


                <div>

                  <span className="text-slate-500">
                    Günlük
                  </span>

                  <p className="font-black text-slate-800 mt-1">
                    {config.dailyMinutes} dk
                  </p>

                </div>


                <div>

                  <span className="text-slate-500">
                    Ders
                  </span>

                  <p className="font-black text-slate-800 mt-1">
                    {
                      config
                        .selectedSubjects
                        .length
                    } ders
                  </p>

                </div>

              </div>

            </div>

          </div>

        )}


        {step === 4 && (

          <div className="space-y-6">

            <div
              className={`rounded-2xl border p-5 ${
                resultReady
                  ? 'border-emerald-200 bg-emerald-50'
                  : resultPartial
                    ? 'border-amber-200 bg-amber-50'
                    : 'border-rose-200 bg-rose-50'
              }`}
            >

              <div className="flex items-start gap-3">

                {
                  resultReady
                    ? (
                      <CheckCircle2 className="h-6 w-6 text-emerald-600 shrink-0" />
                    )
                    : (
                      <AlertTriangle
                        className={`h-6 w-6 shrink-0 ${
                          resultPartial
                            ? 'text-amber-600'
                            : 'text-rose-600'
                        }`}
                      />
                    )
                }


                <div>

                  <h3 className="text-base font-black text-slate-900">

                    {
                      resultReady
                        ? 'Plan hazır'
                        : resultPartial
                          ? 'Plan kısmen oluşturuldu'
                          : 'Plan oluşturulamadı'
                    }

                  </h3>


                  <p className="text-xs text-slate-600 mt-1">

                    {
                      resultReady
                        ? `${
                            result
                              ?.scheduledSlots
                              ?.length ||
                            0
                          } çalışma bloğu takvime yerleştirildi.`

                        : resultPartial
                          ? `${
                              result
                                ?.scheduledSlots
                                ?.length ||
                              0
                            } blok yerleşti, ${
                              result
                                ?.unscheduledSlots
                                ?.length ||
                              0
                            } blok yerleştirilemedi.`

                          : 'Ayarları kontrol edip yeniden deneyin.'
                    }

                  </p>

                </div>

              </div>

            </div>


            {resultReady && (
  <div className="flex flex-wrap items-center justify-end gap-2">


    <button
      type="button"
      onClick={
        handleUndoEdit
      }
      disabled={
        editHistory.length === 0
      }
      className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-600 text-xs font-black hover:border-indigo-300 hover:text-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 transition"
      title="Son plan değişikliğini geri al"
    >
      <RotateCcw className="h-4 w-4" />

      Geri Al

      {editHistory.length > 0 && (
        <span className="text-[9px] opacity-60">
          ({editHistory.length})
        </span>
      )}
    </button>


    <button
      type="button"
      onClick={
        handleRestoreOriginalPlan
      }
      disabled={
        !Array.isArray(
          originalSchedule
        ) ||
        originalSchedule.length === 0
      }
      className="px-4 py-2.5 rounded-xl border border-indigo-200 bg-indigo-50 text-indigo-700 text-xs font-black hover:bg-indigo-100 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 transition"
      title="Robotun ilk oluşturduğu plana dön"
    >
      <Sparkles className="h-4 w-4" />

      AI Planına Dön
    </button>


     <button
      type="button"
      onClick={handleAssignRequest}
      disabled={
        isAssigning ||
        !resultReady ||
        !selectedStudent
      }
      className="px-4 py-2.5 rounded-xl bg-indigo-600 text-white text-xs font-black hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
    >
      <Send className="h-4 w-4" />

      {
        isAssigning
          ? 'Atanıyor...'
          : assignSuccess
            ? 'Plan Atandı ✓'
            : 'Öğrenciye Ata'
      }
    </button>


        <button
      type="button"
      onClick={() =>
        handlePrintSmartPlan()
      }
      className="px-4 py-2.5 rounded-xl bg-slate-900 text-white text-xs font-black hover:bg-black flex items-center gap-2"
    >
      <Printer className="h-4 w-4" />

      Tüm Planı Yazdır
    </button>

  </div>
)}


{assignWarningConfirmOpen &&
 livePlanWarnings.length > 0 && (

  <div className="rounded-2xl border-2 border-orange-300 bg-orange-50 p-5">

    <div className="flex items-start gap-3">

      <div className="h-10 w-10 rounded-xl bg-orange-100 flex items-center justify-center shrink-0">

        <AlertTriangle className="h-5 w-5 text-orange-600" />

      </div>


      <div className="min-w-0 flex-1">

        <h4 className="text-sm font-black text-orange-900">
          Planı bu uyarılarla öğrenciye atamak üzeresiniz
        </h4>

        <p className="text-[11px] text-orange-700 mt-1 leading-relaxed">
          Rehber360 aşağıdaki noktaları gözden geçirmenizi öneriyor.
          Bunlar teknik bir engel değildir; son karar uzmanındır.
        </p>

        <div className="mt-3 inline-flex items-center rounded-full bg-orange-100 px-2.5 py-1 text-[10px] font-black text-orange-800">
          {livePlanWarnings.length} uyarı
        </div>

      </div>

    </div>


    <ul className="mt-4 space-y-2 max-h-52 overflow-y-auto pr-2">

      {livePlanWarnings.map(
        (warning, index) => (

          <li
            key={`assign-warning-${warning.code}-${warning.weekNumber}-${warning.day}-${index}`}
            className="text-xs font-bold text-orange-900 leading-relaxed"
          >
            • {warning.message}
          </li>

        )
      )}

    </ul>


    <div className="mt-5 flex flex-wrap justify-end gap-2">

      <button
        type="button"
        onClick={() =>
          setAssignWarningConfirmOpen(
            false
          )
        }
        className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-xs font-black hover:bg-slate-50 transition"
      >
        Planı Düzenlemeye Dön
      </button>


      <button
        type="button"
        onClick={
          handleAssignSmartPlan
        }
        disabled={
          isAssigning
        }
        className="px-4 py-2.5 rounded-xl bg-orange-600 text-white text-xs font-black hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition"
      >
        <Send className="h-4 w-4" />

        {
          isAssigning
            ? 'Atanıyor...'
            : 'Yine de Öğrenciye Ata'
        }
      </button>

    </div>

  </div>

)}



            {
              Array.isArray(
                result?.errors
              ) &&
              result.errors.length > 0 &&
              (

                <div className="rounded-2xl border border-rose-200 bg-white p-5">

                  <h4 className="text-sm font-black text-rose-700">
                    Düzeltilmesi gerekenler
                  </h4>

                  <ul className="mt-3 space-y-2">

                    {
                      result.errors.map(
                        (
                          issue,
                          index
                        ) => (

                          <li
                            key={`${issue?.code}-${index}`}
                            className="text-xs font-bold text-slate-600"
                          >
                            • {
                              renderIssue(
                                issue
                              )
                            }
                          </li>

                        )
                      )
                    }

                  </ul>

                </div>

              )
            }


           
                           {visibleWarnings.length > 0 && (
  <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
    <h4 className="text-sm font-black text-amber-800">
      Plan hakkında
    </h4>

    <ul className="mt-3 space-y-2">
      {visibleWarnings.map((issue, index) => (
        <li
          key={`${issue?.code}-${index}`}
          className="text-xs font-bold text-amber-800 leading-relaxed"
        >
          • {renderIssue(issue)}
        </li>
      ))}
    </ul>
  </div>
)}


{hasTopicCoverageRecommendation && (

  <div className="rounded-2xl border border-indigo-200 bg-indigo-50/70 p-5">

    <div className="flex items-start gap-3">

      <div className="h-9 w-9 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0">

        <Target className="h-4 w-4 text-indigo-600" />

      </div>


      <div className="min-w-0 flex-1">

        <h4 className="text-sm font-black text-indigo-900">
          Kapsam önerisi
        </h4>

        <p className="text-[11px] text-indigo-700 mt-1 leading-relaxed">
          Seçtiğiniz konu kapsamının tamamını öğrenme sırasını koruyarak
          plana yerleştirmek için daha uzun bir çalışma süresi öneriliyor.
        </p>

      </div>

    </div>


    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">

      <div className="rounded-xl border border-indigo-100 bg-white px-3 py-2.5">

        <span className="text-[9px] font-black uppercase tracking-wide text-slate-400">
          Mevcut süre
        </span>

        <p className="mt-1 text-sm font-black text-slate-800">
          {
            topicCoverageRecommendation
              ?.currentWeeks ??
            config.weeks
          } hafta
        </p>

      </div>


      <div className="rounded-xl border border-indigo-100 bg-white px-3 py-2.5">

        <span className="text-[9px] font-black uppercase tracking-wide text-slate-400">
          Önerilen minimum
        </span>

        <p className="mt-1 text-sm font-black text-indigo-700">
          {
            topicCoverageRecommendation
              ?.minimumFeasibleWeeks
          } hafta
        </p>

      </div>


      <div className="rounded-xl border border-indigo-100 bg-white px-3 py-2.5">

        <span className="text-[9px] font-black uppercase tracking-wide text-slate-400">
          Konu kapsamı
        </span>

        <p className="mt-1 text-sm font-black text-slate-800">
          {
            topicCoverageRecommendation
              ?.coveredTopicCount ??
            0
          }
          /
          {
            topicCoverageRecommendation
              ?.targetTopicCount ??
            0
          } konu
        </p>

      </div>


      <div className="rounded-xl border border-indigo-100 bg-white px-3 py-2.5">

        <span className="text-[9px] font-black uppercase tracking-wide text-slate-400">
          Kapsama oranı
        </span>

        <p className="mt-1 text-sm font-black text-slate-800">
          %
          {
            Math.round(
              Number(
                topicCoverageRecommendation
                  ?.coveragePercent ||
                0
              )
            )
          }
        </p>

      </div>

    </div>


       <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">

      <p className="text-[10px] font-bold text-indigo-700 leading-relaxed">
        Bu bir zorunluluk değildir. Uzman mevcut süreyi koruyabilir,
        kapsamı daraltabilir veya önerilen süreyi tercih edebilir.
      </p>


      {canApplyCoverageRecommendation && (

        <button
          type="button"
          onClick={
            handleApplyCoverageRecommendation
          }
          className="shrink-0 px-4 py-2.5 rounded-xl bg-indigo-600 text-white text-xs font-black hover:bg-indigo-700 flex items-center justify-center gap-2 transition shadow-sm"
        >
          <Sparkles className="h-4 w-4" />

          Planı {
            coverageRecommendedWeeks
          } haftaya çıkar ve yeniden oluştur
        </button>

      )}

    </div>

  </div>

)}


{livePlanWarnings.length > 0 && (

  <div className="rounded-2xl border border-orange-200 bg-orange-50 p-5">

    <div className="flex items-start gap-3">

      <div className="h-8 w-8 rounded-xl bg-orange-100 flex items-center justify-center shrink-0">

        <AlertTriangle className="h-4 w-4 text-orange-600" />

      </div>


      <div className="min-w-0">

        <h4 className="text-sm font-black text-orange-800">
          Uzman düzenleme kontrolü
        </h4>

        <p className="text-[11px] text-orange-700 mt-1 leading-relaxed">
          Plan kullanılabilir. Aşağıdaki noktaları öğrenciye atamadan önce gözden geçirmeniz önerilir.
        </p>

      </div>

    </div>


    <ul className="mt-4 space-y-2">

      {livePlanWarnings.map(
        (warning, index) => (

          <li
            key={`${warning.code}-${warning.weekNumber}-${warning.day}-${index}`}
            className="text-xs font-bold text-orange-800 leading-relaxed"
          >
            • {warning.message}
          </li>

        )
      )}

    </ul>

  </div>

)}


                        {
              Array.isArray(
                editableSchedule
              ) &&
              editableSchedule.length > 0 &&
              (

                <div className="space-y-5">

                  {
                    editableSchedule.map(
                      week => (

                        <div
                          key={
                            week.weekNumber
                          }
                          className="rounded-2xl border border-slate-200 overflow-hidden"
                        >

                         <div className="px-5 py-3 bg-slate-900 text-white flex items-center justify-between gap-3">

  <h4 className="text-sm font-black">
    {week.weekNumber}. Hafta
  </h4>


  <div className="flex items-center gap-3">

    <span className="text-[10px] font-black uppercase tracking-wider text-slate-300">
      {
        week.days
          ?.length ||
        0
      } çalışma günü
    </span>


    <button
      type="button"
      onClick={() =>
        handlePrintSmartPlan(
          week.weekNumber
        )
      }
      className="px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-[10px] font-black flex items-center gap-1.5 transition"
    >
      <Printer className="h-3.5 w-3.5" />

      Bu Haftayı Yazdır
    </button>

  </div>

</div>


                          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-4 bg-slate-50">

                            {
                              (
                                week.days ||
                                []
                              ).map(
                                day => (

                                  <div
                                    key={`${week.weekNumber}-${day.day}`}
                                    className="rounded-xl bg-white border border-slate-200 p-4"
                                  >

                                    <div className="flex items-center justify-between gap-3 pb-3 border-b border-slate-100">

                                      <div>

                                        <p className="text-xs font-black text-slate-900">
                                          {day.day}
                                        </p>

                                        <p className="text-[10px] text-slate-400 mt-0.5">
                                          {
                                            day.taskCount ||
                                            0
                                          } görev
                                        </p>

                                      </div>


                                      <span className="px-2.5 py-1 rounded-full bg-slate-100 text-[10px] font-black text-slate-600">
                                        {
                                          day.totalMinutes ||
                                          0
                                        } / {
                                          config.dailyMinutes
                                        } dk
                                      </span>

                                    </div>


                                    <div className="space-y-2 mt-3">

                                      {
                                        (
                                          day.tasks ||
                                          []
                                        ).length === 0
                                          ? (

                                            <p className="text-xs text-slate-400 italic">
                                              Bu gün için görev yok.
                                            </p>

                                          )
                                          : (

                                            day.tasks.map(
                                              task => (

                                                <div
                                                                                                    key={
                                                    task.editorId ||
                                                    task.id
                                                  }
                                                  className={`rounded-xl border p-3 ${
                                                    TASK_STYLES[
                                                      task.type
                                                    ] ||
                                                    'border-slate-200 bg-slate-50 text-slate-700'
                                                  }`}
                                                >

                                                  <div className="flex items-start justify-between gap-2">

                                                    <span className="text-[10px] font-black uppercase tracking-wider">
                                                      {
                                                        TASK_LABELS[
                                                          task.type
                                                        ] ||
                                                        task.type
                                                      }
                                                    </span>

                                                                                                        <div className="flex items-center gap-2">

                                                                                                          {task.origin === 'engine' && (
  <span
    className="inline-flex items-center gap-1 rounded-full bg-white/60 border border-current/10 px-1.5 py-0.5 text-[9px] font-black normal-case"
    title={
      task.editedByUser
        ? 'Robot tarafından oluşturuldu, uzman tarafından düzenlendi'
        : 'Robot tarafından oluşturuldu'
    }
  >
    <Sparkles className="h-3 w-3" />

    {
      task.editedByUser
        ? 'Robot · Düzenlendi'
        : 'Robot'
    }
  </span>
)}


{task.origin === 'manual' && (
  <span
    className="inline-flex items-center gap-1 rounded-full bg-white/60 border border-current/10 px-1.5 py-0.5 text-[9px] font-black normal-case"
    title="Uzman tarafından plana eklendi"
  >
    ✎ Uzman
  </span>
)}


<span className="text-[10px] font-black">
  {
    task.duration
  } dk
</span>

                                                    </div>

                                                  </div>


                                                  {task.subject && (
  <p className="text-xs font-black mt-2">
    {task.subject}
  </p>
)}

{task.topic && (
  <p className="text-[11px] font-bold mt-1 opacity-80">
    {task.topic}
  </p>
)}

{renderScopedAssessmentScope(
  task
)}

{TASK_DESCRIPTIONS[task.type] && (  <p className="text-[10px] mt-2 leading-relaxed opacity-70">
    {TASK_DESCRIPTIONS[task.type]}
  </p>
)}

{(task.questionCount || task.resource) && (

                                                      <p className="text-[10px] mt-2 opacity-70">

                                                        {
                                                          task.questionCount
                                                            ? `${
                                                                task.questionCount
                                                              } soru`
                                                            : ''
                                                        }

                                                        {
                                                          task.questionCount &&
                                                          task.resource
                                                            ? ' • '
                                                            : ''
                                                        }

                                                        {
                                                          task.resource ||
                                                          ''
                                                        }

                                                      </p>

                                                    )
                                                  }



                                                                                                <div className="mt-3 pt-2 border-t border-current/10">


  <div className="grid grid-cols-2 gap-2 mb-2">

    <button
      type="button"
      disabled={
        !MANUAL_TASK_TYPE_OPTIONS.includes(
          task.type
        )
      }
      onClick={
        () =>
          openEditTaskForm({
            weekNumber:
              week.weekNumber,

            day:
              day.day,

            task
          })
      }
      className="rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-[10px] font-black text-slate-600 hover:border-indigo-300 hover:text-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
      title={
        MANUAL_TASK_TYPE_OPTIONS.includes(
          task.type
        )
          ? 'Görevi düzenle'
          : 'Bu görev türü henüz hızlı düzenlemeyi desteklemiyor'
      }
    >
      Düzenle
    </button>


    <button
      type="button"
      onClick={
        () =>
          handleRemoveTask({
            weekNumber:
              week.weekNumber,

            day:
              day.day,

            editorId:
              task.editorId
          })
      }
      className="rounded-lg border border-rose-200 bg-white px-2.5 py-2 text-[10px] font-black text-rose-600 hover:bg-rose-50 hover:border-rose-300 transition"
    >
      Kaldır
    </button>

  </div>


  <select
                                                      defaultValue=""
                                                      onChange={
                                                        e => {

                                                          const targetValue =
                                                            e.target.value;


                                                          if (!targetValue) {
                                                            return;
                                                          }


                                                          const [
                                                            targetWeekNumber,
                                                            targetDay
                                                          ] =
                                                            targetValue.split(
                                                              '::'
                                                            );


                                                          handleMoveTask({
                                                            editorId:
                                                              task.editorId,

                                                            targetWeekNumber:
                                                              Number(
                                                                targetWeekNumber
                                                              ),

                                                            targetDay
                                                          });


                                                          e.target.value =
                                                            '';
                                                        }
                                                      }
                                                      className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-[10px] font-bold text-slate-600 focus:outline-none focus:border-indigo-400"
                                                    >

                                                      <option value="">
                                                        Başka güne taşı...
                                                      </option>


                                                      {
                                                        editableSchedule.flatMap(
                                                          targetWeek =>
                                                            (
                                                              targetWeek?.days ||
                                                              []
                                                            )
                                                              .filter(
                                                                targetDayItem =>
                                                                  !(
                                                                    Number(
                                                                      targetWeek?.weekNumber
                                                                    ) ===
                                                                      Number(
                                                                        week?.weekNumber
                                                                      ) &&
                                                                    targetDayItem?.day ===
                                                                      day?.day
                                                                  )
                                                              )
                                                              .map(
                                                                targetDayItem => (

                                                                  <option
                                                                    key={`${
                                                                      targetWeek?.weekNumber
                                                                    }-${
                                                                      targetDayItem?.day
                                                                    }`}
                                                                    value={`${
                                                                      targetWeek?.weekNumber
                                                                    }::${
                                                                      targetDayItem?.day
                                                                    }`}
                                                                  >

                                                                    {
                                                                      editableSchedule.length > 1
                                                                        ? `${
                                                                            targetWeek?.weekNumber
                                                                          }. Hafta · `
                                                                        : ''
                                                                    }

                                                                    {
                                                                      targetDayItem?.day
                                                                    }

                                                                  </option>

                                                                )
                                                              )
                                                        )
                                                      }

                                                                                                       </select>

                                                  </div>

                                                </div>

                                              )
                                            )

                                          )
                                      }

                                    </div>


                                    {/* ============================= */}
                                    {/* MANUEL GÖREV EKLEME ALANI */}
                                    {/* ============================= */}

                                    <div className="mt-3 pt-3 border-t border-slate-100">

                                      {
                                        manualTaskTarget &&
                                        Number(
                                          manualTaskTarget.weekNumber
                                        ) ===
                                          Number(
                                            week.weekNumber
                                          ) &&
                                        manualTaskTarget.day ===
                                          day.day
                                          ? (

                                            <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-3 space-y-3">

                                              <div className="flex items-center justify-between gap-3">

                                                <div>

                                                                                                   <p className="text-xs font-black text-indigo-900">
                                                    {
                                                      manualTaskTarget?.mode === 'edit'
                                                        ? 'Görevi düzenle'
                                                        : 'Yeni görev ekle'
                                                    }
                                                  </p>

                                                  <p className="text-[10px] text-indigo-600 mt-0.5">
                                                    {
                                                      manualTaskTarget?.mode === 'edit'
                                                        ? `${day.day} günündeki görevi değiştiriyorsunuz`
                                                        : `${day.day} için uzman görevi`
                                                    }
                                                  </p>

                                                </div>


                                                <button
                                                  type="button"
                                                  onClick={
                                                    closeManualTaskForm
                                                  }
                                                  className="text-[10px] font-black text-slate-500 hover:text-slate-800"
                                                >
                                                  Vazgeç
                                                </button>

                                              </div>


                                              <div className="grid grid-cols-1 gap-2">

                                                <div>

                                                  <label className="block text-[9px] font-black text-slate-500 mb-1">
                                                    Çalışma türü
                                                  </label>

                                                  <select
                                                    value={
                                                      manualTaskDraft.type
                                                    }
                                                    onChange={
                                                      e =>
                                                        setManualTaskDraft(
                                                          prev => ({
                                                            ...prev,
                                                            type:
                                                              e.target.value
                                                          })
                                                        )
                                                    }
                                                    className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-[10px] font-bold text-slate-700"
                                                  >

                                                    {
                                                      MANUAL_TASK_TYPE_OPTIONS.map(
                                                        type => (

                                                          <option
                                                            key={type}
                                                            value={type}
                                                          >
                                                            {
                                                              TASK_LABELS[type] ||
                                                              type
                                                            }
                                                          </option>

                                                        )
                                                      )
                                                    }

                                                  </select>

                                                </div>


                                                {manualTaskDraft.type !== 'buffer' && (

                                                  <>

                                                    <div>

                                                      <label className="block text-[9px] font-black text-slate-500 mb-1">
                                                        Ders
                                                      </label>

                                                      <select
                                                        value={
                                                          manualTaskDraft.subject
                                                        }
                                                        onChange={
                                                          e => {

                                                            const nextSubject =
                                                              e.target.value;

                                                                                                                        const nextTopics =
                                                              getManualTaskTopics(
                                                                nextSubject
                                                              );


                                                            setManualTaskDraft(
                                                              prev => ({
                                                                ...prev,

                                                                subject:
                                                                  nextSubject,

                                                                topic:
                                                                  nextTopics[0] ||
                                                                  ''
                                                              })
                                                            );
                                                          }
                                                        }
                                                        className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-[10px] font-bold text-slate-700"
                                                      >

                                                        {
                                                          config.selectedSubjects.map(
                                                            subject => (

                                                              <option
                                                                key={subject}
                                                                value={subject}
                                                              >
                                                                {subject}
                                                              </option>

                                                            )
                                                          )
                                                        }

                                                      </select>

                                                    </div>


                                                    <div>

                                                      <label className="block text-[9px] font-black text-slate-500 mb-1">
                                                        Konu
                                                      </label>

                                                      <select
                                                        value={
                                                          manualTaskDraft.topic
                                                        }
                                                        onChange={
                                                          e =>
                                                            setManualTaskDraft(
                                                              prev => ({
                                                                ...prev,

                                                                topic:
                                                                  e.target.value
                                                              })
                                                            )
                                                        }
                                                        className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-[10px] font-bold text-slate-700"
                                                      >

                                                                                                              {
                                                          getManualTaskTopics(
                                                            manualTaskDraft.subject
                                                          ).map(
                                                            topic => (

                                                              <option
                                                                key={topic}
                                                                value={topic}
                                                              >
                                                                {topic}
                                                              </option>

                                                            )
                                                          )
                                                        }

                                                      </select>

                                                    </div>

                                                  </>

                                                )}


                                                <div className="grid grid-cols-2 gap-2">

                                                  <div>

                                                    <label className="block text-[9px] font-black text-slate-500 mb-1">
                                                      Süre
                                                    </label>

                                                    <input
                                                      type="number"
                                                      min="15"
                                                      max="600"
                                                      step="15"
                                                      value={
                                                        manualTaskDraft.duration
                                                      }
                                                      onChange={
                                                        e =>
                                                          setManualTaskDraft(
                                                            prev => ({
                                                              ...prev,

                                                              duration:
                                                                e.target.value
                                                            })
                                                          )
                                                      }
                                                      className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-[10px] font-bold text-slate-700"
                                                    />

                                                  </div>


                                                  <div>

                                                    <label className="block text-[9px] font-black text-slate-500 mb-1">
                                                      Soru sayısı
                                                    </label>

                                                    <input
                                                      type="number"
                                                      min="0"
                                                      step="1"
                                                      placeholder="İsteğe bağlı"
                                                      value={
                                                        manualTaskDraft.questionCount
                                                      }
                                                      onChange={
                                                        e =>
                                                          setManualTaskDraft(
                                                            prev => ({
                                                              ...prev,

                                                              questionCount:
                                                                e.target.value
                                                            })
                                                          )
                                                      }
                                                      className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-[10px] font-bold text-slate-700"
                                                    />

                                                  </div>

                                                </div>


                                                <div>

                                                  <label className="block text-[9px] font-black text-slate-500 mb-1">
                                                    Kaynak
                                                  </label>

                                                  <input
                                                    type="text"
                                                    placeholder="İsteğe bağlı kaynak..."
                                                    value={
                                                      manualTaskDraft.resource
                                                    }
                                                    onChange={
                                                      e =>
                                                        setManualTaskDraft(
                                                          prev => ({
                                                            ...prev,

                                                            resource:
                                                              e.target.value
                                                          })
                                                        )
                                                    }
                                                    className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-[10px] font-bold text-slate-700"
                                                  />

                                                </div>


                                                                                                <button
                                                  type="button"
                                                  onClick={
                                                    manualTaskTarget?.mode === 'edit'
                                                      ? handleUpdateTask
                                                      : handleAddManualTask
                                                  }
                                                  className="w-full rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2.5 text-[10px] font-black transition"
                                                >
                                                  {
                                                    manualTaskTarget?.mode === 'edit'
                                                      ? 'Değişiklikleri Kaydet'
                                                      : 'Görevi Ekle'
                                                  }
                                                </button>

                                              </div>

                                            </div>

                                          )
                                          : (

                                            <button
                                              type="button"
                                              onClick={
                                                () =>
                                                  openManualTaskForm({
                                                    weekNumber:
                                                      week.weekNumber,

                                                    day:
                                                      day.day
                                                  })
                                              }
                                              className="w-full rounded-xl border-2 border-dashed border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/40 px-3 py-2.5 text-[10px] font-black text-slate-500 hover:text-indigo-700 transition"
                                            >
                                              + Görev Ekle
                                            </button>

                                          )
                                      }

                                    </div>

                                  </div>

                                )
                              )
                            }

                          </div>

                        </div>

                      )
                    )
                  }

                </div>

              )
            }

          </div>

        )}

      </div>


      <div className="px-5 md:px-7 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between gap-3">

        <button
          type="button"
          disabled={
            step === 0
          }
          onClick={
            () =>
              setStep(
                prev =>
                  Math.max(
                    0,
                    prev - 1
                  )
              )
          }
          className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-xs font-black text-slate-600 disabled:opacity-40 flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />

          Geri
        </button>


        {step < 3 && (

          <button
            type="button"
            disabled={
              !canContinue()
            }
            onClick={
              () =>
                setStep(
                  prev =>
                    prev + 1
                )
            }
            className="px-5 py-2.5 rounded-xl bg-indigo-600 text-white text-xs font-black hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
          >
            Devam Et

            <ArrowRight className="h-4 w-4" />
          </button>

        )}


        {step === 3 && (

          <button
            type="button"
            onClick={
              handleGenerate
            }
            className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-xs font-black hover:from-indigo-700 hover:to-violet-700 flex items-center gap-2 shadow-sm"
          >
            <Brain className="h-4 w-4" />

            Akıllı Planı Oluştur
          </button>

        )}


        {step === 4 && (

          <button
            type="button"
            onClick={
              () =>
                setStep(3)
            }
            className="px-5 py-2.5 rounded-xl bg-slate-900 text-white text-xs font-black hover:bg-black flex items-center gap-2"
          >
            <BookOpen className="h-4 w-4" />

            Ayarları Düzenle
          </button>

        )}

      </div>

    </div>
  );
};

export default SmartPlanBuilder;